// =============================================================================
// CACHE VERSION — bump this to invalidate ALL localStorage caches on deploy
// =============================================================================
// When you fix a query, change a metric calculation, or update an RPC, bump
// this version. Every client's next page load will cache-miss (old keys don't
// match) and fetch fresh data automatically.
//
// On startup, evictStaleCaches() removes ALL altara_* keys from prior versions.
// =============================================================================

// Auto-bump: CACHE_VERSION is now tied to __BUILD_COMMIT__ (git SHA, injected by vite.config.ts).
// Every deploy gets a unique version automatically — no more manual bumps.
// The v4-v68 comments below are historical changelog, not the current mechanism.
declare const __BUILD_COMMIT__: string;

// v62: Apr 6 hardening aftermath — prestige backfill (Mar 20 → Apr 10) changed
//       prestige_points.total_points and prestige_ranks.current_level for 8
//       Rayston reps (Beryl closer L1, Patil setter L1 retroactively promoted).
//       Browsers may still serve level 0 / stale points cached before Apr 10.

// v63: Calendly Phase 2 — CalendlyStats.totalCanceled renamed to
//       cancellations.total; added cancelReasons. Cached Calendly analytics
//       would read the old shape and crash.

// v64: Dialer tracker foundation — new dial_events/dial_sessions/lead_assignments/
//       user_shifts/org_routing_config/dial_trees/dial_tree_versions tables, plus
//       leads.lane + profiles.wrap_up_until + profiles.dialer_timezone +
//       organizations.config_overrides columns. dialer_daily_summary VIEW +
//       rollup_dialer_day RPC feed daily_leaderboard via UPSERT on the four
//       dialer-specific fields. Cached dashboard shapes predating these
//       columns would read undefined fields.

// v65: Dialer write paths live — handleDialOutcome now INSERTs into dial_events
//       (dial_attempt + disposition rows per outcome chip click). handleDrop
//       UPDATEs leads.lane in Supabase. Both paths previously local-state only.

// v66: RLS policy changes on 7 tables + 1 view fix. Invalidate all cached
//       queries so dialers get fresh data reflecting updated access rules.

// v67: Calendar tz calculation logic changed — day-column framework, month grid,
//       MiniCalendar "today", and initial currentDate now route through
//       getPartsInTZ instead of browser-local Date arithmetic. Cached enrichment
//       or event-bucket keys computed under the old browser-tz paths would not
//       match new org-tz keys.

// v68: Instagram DMs opened to all reps + tunnel vision filter removed from
//       useInstagramDM. Cached conversation lists keyed under the old forced
//       assignedTo would show a filtered view until CacheVersion eviction.

// v69: Client portal CRUD migrated from N8N webhook to direct Supabase writes.
//       RLS policies applied to client_tasks, client_files, client_communications.
//       No cached read shapes changed — write path only. Bump forces eviction of
//       any portal data cached under stale keys before RLS was applied.

// v70: search_transcript_turns RPC now returns lead_id + lead_name + lead_email
//       (JOIN through fathom_transcripts.transaction_id → transactions → leads).
//       Cached transcript-search results from the old 11-column shape would read
//       undefined for the three new fields, breaking the click-to-LeadTimelineModal
//       flow added to TranscriptMatchCard. Auto-bumped via __BUILD_COMMIT__.

// v71: get_dm_samples new_dm branch now returns ALL conversations started in
//       range (any source), matching daily_dm_metrics.sql:384 hero card SSOT.
//       Was silently filtering to first_sender='lead' only, hiding setter- and
//       ManyChat-initiated conversations while the hero total still counted them.
//       Plus DMSampleModal rendered as 3x3 card grid (ProofGridView visual).

// v72: DMHealthTab dayCount fence-post fixed (single-day range showed X/2 not
//       X/1); V2 Inbound Funnel column relabeled "New DMs" → "Inbound" and
//       drill-down wired to new get_dm_samples 'inbound_new_dm' sample type
//       (lead-first only, matches daily_dm_metrics.sql:673-678 funnel filter).
//       Hero NEW DMS (all sources) and funnel INBOUND (lead-first) no longer
//       share a label.

// v73: search_transcript_turns RPC signature extended from 5 args to 7 —
//       p_exclude_emails (text[]) and p_exclude_recordings_with (text[]) added
//       for per-speaker turn/call muting in CallSearchPage. Empty arrays coerced
//       to null so the RPC's IS NULL fast-path fires on unfiltered searches.
//       useTranscriptSearch forwarded params + deps updated. Cached results from
//       the 5-arg shape silently served wrong rows if either filter was active.
//       Auto-bumped via __BUILD_COMMIT__.

// v74: refetchTasks in ClientPortalPage.tsx now embeds profiles:assigned_to(full_name)
//       matching the canonical page-load query in ClientPortalContext.tsx. The
//       previous .select("*") returned no FK join, so task.profiles became
//       undefined after every Kanban drag mutation — assignee chip silently
//       disappeared. Auto-bumped via __BUILD_COMMIT__.

// v75: useSetterPerformance no longer overrides total_reply_turns with the live
//       get_reply_turn_distribution RPC. That RPC returns a lifetime SUM(c.reply_turns)
//       for any conversation that touched the date range — fine for the histogram
//       BUCKETS and their avg indicator (both lifetime-scoped), but catastrophic
//       for SetterScorecard's msgs/turn ratio (numerator date-scoped, denominator
//       lifetime → showed 0.9 / 1.2 / 0.8 for Norman / Alice / Patil on Apr 25,
//       structurally impossible since every reply turn includes ≥1 setter message).
//       Cached metric blobs from before this fix carry the wrong total_reply_turns;
//       __BUILD_COMMIT__ eviction surfaces correct date-scoped values on next load.

// v76: WK17 alignment shipped (P0.8 Phases 2 + 3). engaged now means "human
//       setter sent ≥1 msg" globally (lead-msg gate dropped from
//       compute_conversation_metadata + backfilled across all conversations).
//       setter_message_count human-only globally (ManyChat excluded — both
//       compute and the live trigger). engagementRate denominator switched
//       to totalNewDMs (was totalNewDMs + outbound_attempts). ghostRate
//       denominator switched to totalManualOutbound (was totalOutboundAttempts
//       — ManyChat-no-reply now correctly excluded as the negligence signal).
//       msgs/hr in SetterScorecard switched to total_work_minutes denominator
//       (was avg_work_mins_per_day × dayCount, broke on patchy attendance).
//       Methodology modal "Engaged" copy updated to match. responseRate
//       PARKED comment added. Cached metrics from before v76 carry old
//       formulas — auto-eviction via __BUILD_COMMIT__.

// v77: get_rep_portal_data role-based commission defaults collapsed to a single
//       uniform fallback (NULL → 0.10). Per-rep rate now lives entirely in
//       organization_members.commission_base — no role branching. Explicit zero
//       still respected for dialers (Alyssa/Samantha). Norman backfilled
//       0 → 0.05 (setter). Cached commission numbers from before this change
//       could show 0% for any rep with commission_base = 0; auto-eviction via
//       __BUILD_COMMIT__ on next deploy.

// v78: get_setter_orphan_pre_call_details now returns ghl_conversations.id
//       (Supabase internal UUID) instead of ghl_conversation_id (GHL external
//       TEXT). ConversationViewModal queries ghl_messages.conversation_id (UUID)
//       and ghl_conversations.id (UUID); previously the orphan drill-through
//       passed a TEXT GHL ID into UUID columns → "Failed to fetch messages"
//       error, 0 turns, empty modal. Cached orphan detail rows from before this
//       fix carry the wrong ID type — auto-eviction via __BUILD_COMMIT__.

// v79: useOperationsAnalytics filteredTransactions / previousPeriodTransactions
//       rewritten to Schwartzian transform. Old filter+sort allocated ~1.7M Date
//       objects per useMemo run on 28K-row org (3 new Date() per row in filter +
//       2 per comparison in O(N log N) sort + ~168K toOrgStartISO/EndISO calls
//       inside the loop). GC pressure stalled the main thread for hundreds of
//       ms — invisible on flame charts, attributed to "browser overhead." Now:
//       boundaries hoisted out of loop (1 call each), Date.parse → number per
//       row (no Date allocation), numeric sort comparator (zero Date in sort).
//       ~70× reduction in Date allocations. Also adds currentOrganization?.timezone
//       to useMemo deps — latent correctness bug where org-switch with same
//       dateRange used stale tz boundaries. Auto-eviction via __BUILD_COMMIT__.

// v80: OrphanPreCallsModal rewritten as a 4-column "Infinity Gauntlet" journey
//       audit (orphan list | lead timeline | reply timeline | chat bubbles).
//       Replaces the prior "click row → nested ConversationViewModal" flow.
//       Auto-selects the first actionable orphan, machine-guns through the
//       remaining ones via a sequence counter (latest click wins), auto-scrolls
//       chat to the message closest to scheduled_call_time. No new RPCs — reuses
//       get_setter_orphan_pre_call_details (col 1), get_lead_timeline (col 2),
//       direct ghl_messages query + ReplyTimeline component (col 3), ChatBubble
//       (col 4). Scoped to orphan pre-calls only because right 3 columns anchor
//       on lead.email — only orphans guarantee email via pre-call form gate.

// v81: Leaderboard ETL + scorer corrected against /metrics SSOT (5 drifts).
//       (1) date anchor switched from event_date::DATE to
//       (COALESCE(call_start_time, event_date) AT TIME ZONE org_tz)::DATE so
//       Saturday-filed Friday calls land on the correct day; (2) paying_installment
//       no longer counted as a "show" or "call taken" (35 such rows in April for
//       Rayston were silently inflating shows + calls_taken across 5 reps);
//       (3) daily_leaderboard.calls_booked now populated from sales_pre_call by
//       setter_id (was 0 for 83% of setter rows, falling back to a shows+no_shows
//       proxy that punished setters whose bookings cancelled); (4) CPC denominator
//       changed from calls_taken to shows for closer + dialer (no-shows no longer
//       deflate CPC); (5) new orphan_precalls column on daily_leaderboard;
//       show_rate generated column rebuilt to use shows/(shows+no_shows+orphan_precalls)
//       so display matches ranking; cpc generated column rebuilt to use shows
//       denominator. CalculationsDrawer.computeBreakdown updated to mirror.
//       April backfill re-ran recalculate_daily_leaderboard for all days +
//       rollup_prestige_points refreshed monthly totals before May 1 lock-in.
//       Cached leaderboard rows from before this commit carry the old wrong
//       numbers — auto-eviction via __BUILD_COMMIT__ on next deploy.

// v82: get_setter_orphan_pre_calls + get_setter_orphan_pre_call_details — two
//       SCO-match bugs fixed. (1) SCO existence check switched from strict
//       call_start_time equality to email-based with sco.event_date >=
//       pc.event_date - 7 days. Pre-call form stores midnight-truncated
//       call_start_time while Calendly events store the actual slot, so
//       equality structurally fails — leads who paid in full got flagged as
//       orphan (e.g. Jayden Nguyen Apr 27 SGT, paid_in_full $5000, was
//       showing in Norman's neglected list). (2) Cancellation diagnosis lookup
//       switched from custom_data->>'call_cancel_by' (always NULL — 0/240
//       cancellations populate the JSON path) to top-level cc.call_cancel_by
//       column (240/240 populated). Pre-fix: every cancelled-pre-call
//       mislabeled "neglected"; post-fix: Alice 14d went from "11 neglected"
//       to "5 neglected, 3 flakey, 3 ghost" — the taxonomy works again.
//       Cancellation lookup also widened from strict call_start_time equality
//       to email + ±14d event_date proximity (closest by absolute time).
//       Norman's count dropped 3→2, Alice's 13→11. Cached orphan rows from
//       before this fix carry the wrong classification — auto-eviction via
//       __BUILD_COMMIT__ on next deploy.

// v85: Lost Pre-Calls tile + orphan range bug defensive error-flag layer.
//       (1) Added lost_pre_calls_count + lost_pre_calls_error to SetterMetrics
//       fed by new get_setter_lost_pre_calls + get_setter_lost_pre_call_details
//       RPCs (event_type='sales_call_outcome' AND status='lost', 7-day backwards
//       SCO match, supersession exclusion of later PIF/CPP wins). Tile sub-stat
//       under Pre-Calls (red, mirrors orphan amber chip). LostPreCallsModal
//       forked from OrphanPreCallsModal — same 4-col shell, diagnosis chip =
//       red LOST + sales_program_sold pill (or italic "Program not captured").
//       (2) Added orphan_pre_calls_error + lost_pre_calls_error flags propagated
//       from useSetterPerformance.ts → tiles render "—" instead of silent 0
//       on RPC failure. This cross-cutting pattern eliminates the dominant
//       DM Analytics failure mode (silent-zero on RPC error, masked auth-role
//       8s timeout in browser context). Auto-evicts via __BUILD_COMMIT__.
//       NOTE: The originally-spec'd covering index for orphan perf was DROPPED
//       after Phase 0 EXPLAIN ANALYZE proved the orphan RPC already runs in
//       210ms at month range — no perf issue, the screenshotted bug was
//       fixed by session 16's SCO email-match rewrite.

// v84: trg_resolve_setter_attribution deployed. BEFORE INSERT OR UPDATE on
//       transactions for sales_call_outcome rows; authoritative setter_id
//       resolver enforcing setter > dialer rule. Reads sales_pre_call rows
//       for same lead_id (with email fallback), joins to organization_members.role,
//       picks setter over dialer; within role, most-recent pre-call wins.
//       No pre-call exists → setter_id = NULL. Logs every overwrite to new
//       setter_attribution_log table (transaction_id, closer_id, prev/new
//       setter_id + name + role, precall_count) for closer-mistype tracking.
//       Going-forward only — no historical backfill of the 7 disagreements,
//       11 nullable-fillable, or 183 no-pre-call-but-has-setter rows that
//       the dry-run surfaced. Closer-typed setter on PostCallOutcomeModal is
//       now silently overridden if it disagrees with the pre-call rule. Cached
//       attribution-keyed dashboard rows from before this trigger may still
//       show the old (closer-typed) setter — auto-eviction via __BUILD_COMMIT__.

// v83: evaluate_daily_kpi_compliance.sql RULE ZERO fix. All 15 bare
//       `event_date::DATE` filters on call-event types replaced with
//       `(COALESCE(call_start_time, event_date) AT TIME ZONE v_tz)::DATE`.
//       v_tz fetched from organizations.timezone at function entry.
//       Mirrors leaderboard ETL fix pattern (583155a). Verified live: closer
//       6a06c6c1 PIF transaction 5f4c8c99 correctly moves from Apr-27
//       (event_day_sgt) to Apr-26 (call_day_sgt) — hit_kpi flips true on
//       Apr-26 (100% show, 60% close), false on Apr-27 (16.7% show).
//       Pre-fix scoring was wrong on both days for ~55% of Rayston call
//       events with column-mismatched dates. KPI cron schedule (P0b) is
//       safe to enable now that the function is correct.

// v86: Leaderboard L0 guard flipped from > 0 to >= 0 in both Podium (rank &&
//       rank.current_level) and SectorTable (prestige && prestige.current_level).
//       Previously L0 reps showed no PrestigeBadge on the Leaderboard page despite
//       the gray "—" badge existing in PrestigeBadge config. Rendered row set
//       changes for every L0 rep — cache bust required.
//       Also: Podium rep-info block gets min-h-[72px] + justify-end so rank-1's
//       taller text block aligns its bottom with rank-2/3 (pedestal alignment fix).

// v87: DateRangePicker — added "This Year" and Q1/Q2/Q3/Q4 calendar-year/quarter
//       presets to mobile chip rail and desktop sidebar. utils/dateRange.ts patched
//       with isCalendarYear() + isCalendarQuarter() branches in navigateDateRange so
//       prev/next from "This Year" jumps a full calendar year and prev/next from
//       Q1-Q4 jumps a full calendar quarter (with year-boundary wrap: Q1 prev →
//       prior year Q4, Q4 next → next year Q1). No localStorage shape change.
//       Cached KPI responses keyed under prior date ranges still valid; bump forces
//       eviction so users see fresh numbers under new preset selections immediately.
//       Auto-bumped via __BUILD_COMMIT__.

// v88: get_mvp_voting_status RPC shape changed — voters[] removed (anonymity spec,
//       2026-04-28). New caller-scoped fields: has_voted (boolean), my_nominee_id
//       (UUID|null), my_nominee_name (text|null). MvpVotingStatus interface updated
//       to match. Cached status objects from before this deploy carry the old
//       voters[] key and lack the new fields — auto-eviction via __BUILD_COMMIT__.

// v89: Apr 28, 2026 — TransactionContext cache key includes date range (silo fix wave 2).
//       Old key: altara_{v}_transactions_data_{prefetchDays}
//       New key: altara_{v}_transactions_{orgId}_{startISO}_{endISO}
//       Same-size windows with different bounds (e.g. "Last 7 days" vs "Apr 1-7")
//       were silently sharing a cache slot. New key encodes the actual range so a
//       date change triggers a real re-fetch. dateRangeBounds state added to
//       TransactionProvider; handleDateRangeChange now updates bounds on every
//       DATE_RANGE_CHANGE_EVENT, not just when prefetchDays needs to grow.

// v97: May 2, 2026 — P0f leaderboard date-filter data-correctness fix.
//       useLeaderboard converted to fully-controlled (filters read directly from
//       props via useMemo; no internal useState). aggregateLeaderboard removed
//       from the hook — page-side computeSectorEntries handles aggregation.
//       Pre-fix, switching date ranges silently dropped reps whose latest fetched
//       date fell outside the new filter (e.g. "All April" view of Setters tab
//       showed only Norman, with Alice + Patil hidden because their latest
//       fetched-window dates landed in May). LeaderboardPage AggregatedEntry now
//       threads orphan_precalls (P0f-followup) so range-mode show_rate denominator
//       matches /metrics canonical (shows / (shows + no_shows + orphan_precalls)).
//       LeaderboardEntry interface gained orphan_precalls?: number to surface the
//       column already emitted by daily_leaderboard_readable. Auto-bumped via
//       __BUILD_COMMIT__ on commit; this changelog entry is documentation only.

// v96: May 1, 2026 — Show Rate forward-looking SCO migration. Killed
//       precall_outcome_matches dependency (frozen Feb 13, 2026) in
//       recalculate_daily_leaderboard.sql STEP 3.5 + 3.6. get_operations_kpis.sql
//       orphan_pre_calls block migrated from window-bounded SCO to forward-looking
//       (sco.call_start_time >= pc.call_start_time) + future-call_booked exemption
//       + right-censoring gate (pc.call_start_time < today, org tz). Status filters
//       on closer + setter INSERT switched from deny-list to explicit allow-list
//       ('paid_in_full','closed_payment_plan','lost','rescheduled','no_show'),
//       silently excludes unique/abandoned_payment_plan/restored_from_abandon/LTP-paid.
//       LTP rows previously contaminating calls_taken (~481 rows since Feb 13)
//       now correctly excluded. Backfill of daily_leaderboard Feb 13 → today
//       + re-run rollup_prestige_points for March + April rolled out in same PR.
//       Mako rulings May 1, 2026 — see /metrics SKILL.md canonical Show Rate
//       definition + Methodology Notes section.

// v95: Apr 30, 2026 — DM metrics now exclude unassigned conversations.
//       get_engaged_per_setter no longer surfaces the (Unassigned) bucket —
//       setters can't see unassigned conversations in the GHL inbox, so they
//       shouldn't surface in DM Analytics. get_dm_analytics_fast hero counts
//       (newDMs lead-replied + engaged) filter ghl_assigned_user_id IS NOT NULL.
//       GHL Split-Leads workflow re-fires on every inbound when assignedTo is
//       null — unassigned self-heals on the next reply; no Altara-side safety
//       net needed. Apr Rayston: 113 noise rows hidden from charts.

// v94: Apr 30, 2026 — Per-setter engagement breakdown in ENGAGED hero card
//       + reply-turn 1/2/3 mini-chart in CONVERSATIONS STARTED hero card.
//       New RPCs: get_engaged_per_setter, get_setter_unengaged_proof.
//       New hooks: useEngagedPerSetter, useUnengagedProof.
//       New ProofMetricType: unengaged_conversations.
//       Unassigned (NULL setter) bucket pinned at top of bars — shows lead-replied
//       conversations no setter ever owned.
//       Cache from before v94 holds no per-setter data — full re-fetch on first visit.

// v93: Apr 30, 2026 — Lead Timelines Phase 2 polish 2: pending/orphan split,
//       multi-setter display, opaque period divider. LeadRow gained
//       setters: string[] (all distinct pre-call setters in chronological
//       order) and hasFutureBooking: boolean. bestOutcome can now resolve to
//       'orphan' — re-tagged from 'pending' when no future call_booked exists.
//       Period stats line gained an orphan count. Filter bar gained Orphan
//       button. Period divider background switched from var(--glass-background)
//       (rgba 0.85-0.88) to solid #ffffff — the prior "opaque outer" was 12-15%
//       translucent, defeating the layered fix from 8755e54. Auto-eviction via
//       __BUILD_COMMIT__.

// v92: Apr 29, 2026 — Lead Timelines Phase 2: closer mode + all-person mode.
//       useSetterLeadTimelines hook signature changed (params now include
//       viewMode: 'setter' | 'closer'; return shape renamed setters → people,
//       selectedSetterId → selectedPersonId, totalPreCalls → totalAnchorEvents).
//       Anchor query branched: setter mode → sales_pre_call/setter_id; closer
//       mode → call_booked/closer_id. New synthetic 'all' option fetches every
//       lead in range without person filter. LeadRow gained setterName,
//       closerName, anchorDate fields for cross-attribution display in "all"
//       mode. Outcome attribution stays lead-terminal (not person-credited).
//       No cached read shapes from prior /admin/setter-timelines visits remain
//       valid — auto-eviction via __BUILD_COMMIT__.

// v91: All Time date range bounded to today-730d (GHL data starts 2025-12-16; 730d covers
//       all current Dynamis data). DateRange.isAllTime flag added — persisted in localStorage.
//       prefetchDays capped globally at 737 (730d + 7d buffer). Prev-period KPI fetch skipped
//       on isAllTime to avoid "-100%" compare deltas on empty pre-GHL window. Picker header,
//       nav arrows, and compare label all gate on isAllTime. DateRange type centralized in
//       useGlobalDateRange.ts. Wave 4 of date-range silo fix; see f7fd7d9/5f86826/952ba67.
//       Auto-bumped via __BUILD_COMMIT__.

// v92 (2026-05-01) — Rep portal show rate now canonical: shows / (shows + no_shows
// + orphan_precalls) × 100. Was shows / calls_taken (= shows / (shows + no_shows)),
// missing the orphan pre-calls term. Setter card now displays Calls Set / Wins /
// Close Rate / Show Rate (was Convos/Messages/Leads/Cash — those duplicate
// MiniSetterStats below). SQL: get_rep_portal_data adds period_orphan_precalls
// return column + DROP+CREATE for signature change. Auto-bumped via __BUILD_COMMIT__.
// Setter show rates will drop visibly — the orphan pre-calls were always there,
// just hidden. See docs/handoff/[doing]_REP_PORTAL_FIXES.md (Session 12).

// v91 (2026-04-30) — DM Analytics NEW DMS hero card redefined: now shows lead-replied
// count (conversations created in window where lead has >=1 message) instead of raw
// "any source" count. Total + never-replied gap shown as subtext under the hero number.
// SQL change: get_dm_analytics_fast adds total_new_dms_lead_replied (live count on
// ghl_conversations.first_lead_msg_date IS NOT NULL). Auto-bumped via __BUILD_COMMIT__.
// Old caches showed inflated NEW DMS hero values (10K+ instead of ~1-3K real inbound).

// v90 (2026-04-28) — leaderboard cache prefix is now versioned via CACHE_VERSION.
// Old altara_lb_* keys auto-evict on next deploy via __BUILD_COMMIT__ change.
// Realtime aggregate-mode ticks now debounce a refetch (300ms) via ref-forwarded
// fetchWithCleanup instead of patching state in place. Daily-mode in-place patch
// preserved (correct: row identity is exactly (rep_id, date)).
// See docs/handoff/[doing]_LEADERBOARD_CACHE_TRUTH.md.

// v92 (2026-05-01) — Added `orphanRate` field to PortalKpis (was missing previously,
//                    now sourced from get_operations_kpis.orphan_rate). Stale cached
//                    portal_kpis rows lack the field — auto-eviction via __BUILD_COMMIT__
//                    on next deploy.
//                    Also: Funnel + Sales Performance + Upcoming Calls embedded in portal.
//                    BentoMetrics Row 2 + Row 3 retired (duplicated by new embeds).

// v93 (2026-05-01) — FloatingDateRangePicker: useGlobalDateRange Context extends with
//                    pickerVisible/pickerFilterLabel/setPickerOptions. Picker moves from 13
//                    in-page sites to a single global FAB in App.tsx. Leaderboard dateMode tab
//                    removed; Realtime daily preserved via isLiveMode (start === end === today
//                    in org tz). __BUILD_COMMIT__ auto-derives CACHE_VERSION; no manual version
//                    bump needed. Safe-list (altara_global_date_range) unchanged. Orphaned key
//                    altara_lb_dateMode left in place (cosmetic).

// v94 (2026-05-01) — useSetterLeadTimelines early-return guard now calls
//                    setLoading(false). Cross-page SPA nav into /admin/setter-timelines
//                    (relabeled "Pipelines") hit a self-induced race: currentOrganization
//                    is already resolved when the page mounts via in-app navigation, so
//                    the fetch effect set loading=true on the first render before the
//                    viewMode-reset effect cascaded selectedPersonId → null. The re-run
//                    hit the early-return guard which cleared data state but never
//                    reset loading. Spinner stuck forever. Hard refresh hid the bug
//                    because organizationId is null during the first few render cycles
//                    while DataContext hydrates, so the fetch never starts before the
//                    null-flush lands. Fix: setLoading(false) inside the guard.
//                    See /zoomout synthesis 2026-05-01 + /debug Bug #33 family.
//                    Auto-bumped via __BUILD_COMMIT__.

// v97 (2026-05-01) — REVERT FloatingDateRangePicker. useGlobalDateRange Context contracts back to
//                    { dateRange, setDateRange, navigateDateRange } (3 fields). pickerVisible /
//                    pickerFilterLabel / setPickerOptions removed. Picker moves from a single
//                    global FAB back to per-page inline render on 8 Tier 1 surfaces (matches
//                    pre-AFK 80088a5 shape). Hide-FAB useEffects deleted from DM/IG/Calendly/
//                    RepPortal. Layout-route refactor considered but rejected per RD2 (this handoff).
//                    __BUILD_COMMIT__ auto-derives CACHE_VERSION; no manual bump.
//                    NOTE: pickerVisible/pickerFilterLabel/setPickerOptions were React-state-only
//                    (never persisted to localStorage), so this revert does NOT change any
//                    localStorage shape. The auto-derived CACHE_VERSION bump on next deploy is
//                    sufficient even though no localStorage data is shape-changed by this revert
//                    — it covers the transitive effect of the deploy itself.
//                    Supersedes v93 (2026-05-01, L361) which documented the now-reverted FAB shift.
//                    Note: numbering jumps to v97 because parallel sessions wrote v94/v95/v96
//                    independently while this revert was being planned. Chronology preserved.

// v98 (2026-05-02) — get_rep_portal_data RPC rewritten to compute period_shows /
//                    period_no_shows / period_wins / period_losses / period_orphan_precalls
//                    inline from `transactions`, bypassing daily_leaderboard. Reason: the
//                    leaderboard ETL has two SSOT violations vs /metrics canonical — (1)
//                    dl.shows uses a deny-list so status='unique' silently lands in shows,
//                    (2) dl.orphan_precalls reads from precall_outcome_matches (frozen
//                    Feb 13, 2026), over-counting orphans by ~10x. Inline orphan logic
//                    implements all five canonical gates: right-censoring, forward-looking
//                    SCO, future-`call_booked` exemption, multi-role pair preservation,
//                    status='unique' exclusion. Closer attribution = closer on LATEST
//                    call_booked (rule f). Verified Apr 2026 Rayston: Beryl 37.6→72.3,
//                    Patil 44.4→86.2, Samantha 35.5→76.9 — show rates were artificially
//                    LOW, not high. Auto-bumps via __BUILD_COMMIT__.
//                    Followup: rewrite recalculate_daily_leaderboard STEP 3.5+3.6 with
//                    same canonical rules + backfill from Feb 13 so leaderboard agrees.

// v99 (2026-05-02) — Closer leaderboard role gate. recalculate_daily_leaderboard.sql
//                    STEP 2 (closer INSERT) now filters by COALESCE(om.role, 'closer')
//                    = 'closer'. Mirrors STEP 3's existing role gate for setters/dialers.
//                    Pre-fix, org owners (role='client') with closer-attributed
//                    transactions bubbled onto the closer leaderboard — Rayston himself
//                    was rank #2/#3 closer in March/April. Post-fix: 9 stale rows
//                    removed from daily_leaderboard, 2 stale prestige_points rows
//                    deleted (Rayston Mar=21pts + Apr=18pts), 3 stale kpi_daily_compliance
//                    rows + 1 stale prestige_ranks row swept. Snapshots:
//                    daily_leaderboard_snapshot_pre_client_filter (607 rows pre),
//                    prestige_points_snapshot_pre_client_filter (26 rows pre).
//                    Beryl April closer rank #1 (116 pts) preserved; Adrian #2 (88 pts).
//                    78-day backfill Feb 13 → today + March/April prestige re-roll
//                    + re-rank shipped in same execute_sql session. Auto-bumps via
//                    __BUILD_COMMIT__.
// v96 (2026-05-02) — OutcomeSummaryStrip rewritten as dynamic flex-wrap.
//                    Denominator changed from classified-only sum (closed+lost+noshow)
//                    to transactions.length — every visible transaction counts toward
//                    the percentage. Added paying_installment + rescheduled + abandoned
//                    + restored + other as their own buckets with cells. New CSS
//                    var --outcome-installment-text (lighter green than Closed).
//                    Snippet mode pills (was hardcoded 6 fields in OperationsDashboard)
//                    now use the same component via variant='compact'. Bucket defs
//                    centralized in lib/outcomeBuckets.ts (resolves spec correctness
//                    trap #8 — sets duplicated in 3 files). Auto-bumps via __BUILD_COMMIT__.

// v101 (2026-05-02) — usePortalKpis DM Health zeros fix. The hook was reducing
//                     get_dm_analytics_fast.data with row.new_dms / row.real_convos /
//                     row.reply_turn_deep — keys that exist ONLY nested inside
//                     daily_breakdown[] entries, never at the top level. RPC actually
//                     returns ONE summary row with total_new_dms / total_real_convos /
//                     total_deep_convos at root (mirrors useOperationsAnalytics.ts:505-514).
//                     Result: portal NEW DMS / DEEP CONVERSATIONS rendered 0 forever
//                     while admin /admin/dashboard rendered correctly off the same RPC.
//                     Cached zeros from before this fix carry the wrong shape — auto-evicts
//                     via __BUILD_COMMIT__ on next deploy.

// v100 (2026-05-02 AFK) — P0.7 + P0.8 ship. New get_rep_daily_journey RPC
//                          (per-day rank/cash/shows/show_rate/cpc/rank_points
//                          for one rep × range — drives Federer-effect callout).
//                          New period_orphan_cohort table + snapshot_period_orphan_cohort
//                          + get_cohort_recovery RPCs (P0.8 — late-resolutions sidecar).
//                          CalculationsDrawer per-rep card extended: hover tooltips
//                          show formula+computation per metric, day-by-day journey
//                          strip with rank+raw on hover, Federer-effect callout
//                          (led on N of M days, tightest gap day), CohortRecoveryCard
//                          mounted (recovered/in_flight/still_orphan with %).
//                          LeaderboardPage.leaderboardFilters memoized (was new
//                          object + new Date() every render, churning useLeaderboard
//                          internals downstream of useRealtimeSubscription).
//                          Backfill: snapshot_period_orphan_cohort run for Feb-Apr
//                          × both orgs (Rayston Apr: 12 closer rows / 39 setter rows).
//                          Cron NOT scheduled — pending Mako verify of recovery numbers.
//                          Auto-bumps via __BUILD_COMMIT__.

// v102 (2026-05-02) — Pre-call AI summaries persisted to transactions.custom_data
//                     (ai_client_status, ai_closing_readiness, ai_financial_status,
//                     ai_model, ai_processed_at) by N8N workflow ZVU6jwGKB38xVomB
//                     Transform node `precall-12`. AI Processor onError branch posts
//                     paste-ready replay-SQL alert to #ecomwarts-precall on Gemma
//                     failure. LeadTimelineModal renders 4 emoji-headed sections
//                     (📞/💰/🎯/📝) for sales_pre_call events. HIDDEN_CUSTOM_KEYS
//                     gained 5 entries to gate ai_ keys from generic bucketing.
//                     Cached pre-call modals from before this deploy lack the new
//                     keys — auto-eviction via __BUILD_COMMIT__ on next deploy.
//                     Note: numbered v102 because v100 + v101 already existed in this
//                     file (parallel sessions). Spec said v100; collision avoided.

// v103 (2026-05-03) — get_funnel_metrics typeform_qual CTE expanded from 2 cols
//                     to 8 cols (P6 dual-grain): applied_submissions, applied_leads,
//                     qualified_submissions, qualified_leads, dq_submissions, dq_leads
//                     emitted alongside back-compat qualified/dq aliases. SankeyFunnel
//                     Applied + Qualified rows now bind to the new keys with sub-line
//                     "{N} leads" + DQ "X DQ'd · Y leads". Cached bySource.typeform
//                     payloads from before P6 lack the six new keys — Applied row
//                     falls back to leads_in (smaller number) and sub line stays
//                     undefined. Bumping MANUAL_SCHEMA_VERSION evicts via
//                     evictStaleCaches() on next App boot — refresh is sufficient.
//                     This entry doubles as the canonical example of the manual-bump
//                     workflow Mako codified after the Apr 26 + May 3 cache crash-outs.
//                     See commit 3604fb3 for the SQL/frontend payload + 7549410 for
//                     the manual-suffix mechanism.

// v108 (2026-05-05) — Year View Tab on /admin/dashboard. New get_year_overview RPC
//                     (sql/analytics/get_year_overview.sql) returns pre-aggregated
//                     month-by-month cash/revenue + goal data for one year. New tab
//                     renders one row per month using existing RevenueBarChart +
//                     FinancialGraph. RevenueBarChart now accepts an optional
//                     monthLabel prop; default behavior unchanged on Performance
//                     hero. Bumping MANUAL_SCHEMA_VERSION here as the safety net
//                     for any per-org cached structures even though existing RPCs
//                     are unchanged.
//
// v107 (2026-05-03) — Setter DM transparency. RLS on ghl_conversations split:
//                     dialers retain tunnel vision, setters joined the see-all
//                     org-roles branch (`role IN ('owner','admin','exec',
//                     'manager','closer','setter','client','viewer')`).
//                     SetterPerformanceTab.tsx no longer narrows setterIds to
//                     self for setter role (`isSetter` added to early-return).
//                     Cached useSetterPerformance / useEngagedPerSetter /
//                     SetterProofModal payloads under existing keys would still
//                     show "1 scorecard (just me)" for setters until evicted.
//                     Mako 2026-05-03: "i want full transparency". Closers/
//                     dialers, transactions RLS, and rep portal scoping unchanged.
//
// v106 (2026-05-03) — DM funnel restructure: kpis (both useOperationsAnalytics
//                     + usePortalKpis) now expose totalConvosCreated + engaged
//                     fields. kpis.newDMs source switched from r.total_new_dms
//                     (raw) to r.total_new_dms_lead_replied (lead-replied +
//                     assigned subset, per SSOT metricMeta.ts:435 Apr 30
//                     redefinition + Mako 2026-05-03 ruling). useOperationsAnalytics
//                     internal dmStats cache key bumped v2 → v3. SankeyFunnel
//                     DM column at "applied" stage now Total Created + sub
//                     "X new DMs"; "qualified" stage now Engaged + sub "X
//                     started" — mirrors DM Analytics page hero card breakdown.
//                     Visible "New DMs" cards across dashboard will drop by
//                     ~50-60% (raw → lead-replied subset). Show Rate and other
//                     downstream metrics unchanged.
//
// v105 (2026-05-03) — kpis.noShows now includes orphan pre-calls per SSOT
//                     (metricMeta.ts:259). useOperationsAnalytics.ts:774 +
//                     usePortalKpis.ts:245 changed: noShows = SCO no_show +
//                     orphan_pre_calls. Visible "No-Shows" cards on Ops
//                     Dashboard and Client Portal will increase by the orphan
//                     count. showUpRate denominator unchanged (already
//                     correctly included orphans). Bump required because cached
//                     value of kpis.noShows under existing keys is now stale.
//                     Mako ruling 2026-05-03: "what does ssot say? follow that."
//
// v104 (2026-05-03) — P6 follow-up: OperationsDashboard.toSourceMetrics +
//                     ClientPortalPage.toSourceMetrics are allowlist transforms
//                     that explicitly copy specific fields from useFunnelHealth
//                     output into the shape SankeyFunnel consumes. v103 added
//                     the six new typeform-dual-grain keys to the SQL + RPC +
//                     hook + types, but missed these two transforms — so the
//                     keys were silently stripped in transit and SankeyFunnel
//                     read undefined → fell back to leads_in. Result: dashboard
//                     showed APPLIED 72 / QUALIFIED 44 / "93 DQ'd" with no
//                     leads sub-line, even after multiple cache bumps. The cache
//                     was always evicting correctly; the bug was downstream of
//                     the cache. Fix: added the six fields to both transforms
//                     so the keys reach SankeyFunnel as designed. Bumping
//                     MANUAL_SCHEMA_VERSION here even though SQL didn't change,
//                     because the consumed payload shape on the dashboard DID
//                     change (new fields now actually populated). Lesson: when
//                     adding RPC fields, audit allowlist transforms in every
//                     consumer page, not just the leaf component.

// Manual schema-version suffix — bump this string whenever an RPC/query shape
// changes. In production __BUILD_COMMIT__ auto-bumps on every push so this is
// belt-and-suspenders, but in LOCAL DEV the constant resolves to "dev" forever —
// shipping new SQL won't invalidate localStorage. Bumping this value is the
// ONLY way to evict the dev cache without opening DevTools. Any string change
// works; convention is a short tag for the most recent shape change.
// v109 (2026-05-05) — Year tab cash semantics fix (P10): get_year_overview rewritten
//                     to bypass daily_org_metrics and read transactions directly with
//                     event-type-aware anchoring (call_start_time for call-type events,
//                     event_date for the rest), in org timezone. Closes Bug #13 for
//                     Year tab + half of Q26 (UTC vs org tz) for this surface only.
//                     Year tab cash for any month overlap range now matches Performance
//                     hero byte-for-byte; visible May 2026 row drops $59,719 → $51,774
//                     (Rayston, verified equality vs get_operations_kpis). RPC return
//                     shape preserved — zero frontend changes. high_ticket_cash also
//                     tightened to PIF+CPP only (was lumping low_ticket_purchase per
//                     the daily_org_metrics:88-90 inheritance bug).
//                     KNOWN CARRY-OVER: daily_org_metrics still buckets by event_date
//                     AT TIME ZONE 'UTC' for the Performance tab's `dailyChartData`
//                     sparklines (useOperationsAnalytics.ts ~line 628), the
//                     leaderboard ETL, and any other consumer of the rollup. So the
//                     Performance tab HERO now reads $51,774 (via get_operations_kpis
//                     → call_start_time + org_tz) while its daily-bar SPARKLINE
//                     under that hero still distributes by event_date + UTC. Hero
//                     sum and chart sum can disagree until daily_org_metrics is
//                     re-bucketed. Tracked as Q26 follow-up — not in P10's scope.
//                     Cached useYearOverview payloads from v108 will show stale
//                     numbers until evicted — evictStaleCaches() at App boot wipes
//                     altara_* keys on next refresh. See get_operations_kpis.sql:42-97
//                     for the canonical anchoring pattern this RPC mirrors.
// v110-year-tab-day-slice (May 6, 2026, P11)
// ─────────────────────────────────────────────
// get_year_overview RPC gained two optional params: p_day_start INT DEFAULT 1,
// p_day_end INT DEFAULT 31. When changed from defaults, txn_anchored CTE filters
// by EXTRACT(DAY FROM anchor_date) BETWEEN start AND end (org tz). New JSONB
// fields: slice_start_day, slice_end_day on YearOverview; slice_label on each
// YearMonthData. Goal_amount returned as NULL during a slice (monthly goals
// don't prorate cleanly). Future-slice skip rule: hide month if make_date(year,
// m, LEAST(p_day_start, last_day)) > v_today.
// Frontend ADD: localStorage altara_ops_yearTab_dayRange (UI preference, OK to
// wipe on bump). Backward compat: defaults preserve v109 behavior byte-for-byte
// — confirmed via execute_sql equality vs get_operations_kpis P11 Step 3.2.

// v111 (2026-05-07) — get_closer_triage_list RPC deployed (Lost Leads Triage
//                     page). New cache key shape: altara_${CACHE_VERSION}_triage_*
//                     (NOT safe-listed — intentionally evicted on every bump).
//                     Two new RLS policies on transactions:
//                     transactions_insert_general (closers/setters/managers/execs
//                     can INSERT their own org's call outcomes) +
//                     transactions_self_set_claim_insert (reps can claim a self-set
//                     lead without a pre-call). chk_transactions_event_type
//                     constraint extended to allow 'self_set_claim' as a valid
//                     event_type. Schema delta requires cache eviction so triage
//                     page doesn't serve stale/absent RPC payloads from prior shape.
//                     See docs/handoff/[doing]_LOST_LEADS_TRIAGE_PAGE.md Step 11.

// v112-setter-booked-lead-distinct (2026-05-07, P12)
// ─────────────────────────────────────────────────
// THREE coupled changes shipping together (Mako May 7, 2026 ruling):
//   (1) get_operations_kpis.setter_booked redefined from raw event count
//       (COUNT(*) FILTER event_type='sales_pre_call' AND setter_id IS NOT NULL)
//       → COUNT(DISTINCT lead_id) JOIN organization_members WHERE role='setter'.
//       Excludes dialer-only / closer / manager / exec / client pre-calls.
//       Multi-role pairs counted once via setter allow-list. Live verified:
//       Rayston May 1-7 = 94 events → 26 distinct setter-touched leads (3.6× inflation).
//   (2) Call Summary BOOKED card REMOVED (was byte-equivalent to DM Health → SETTER
//       BOOKED — every pre-call has setter_id IS NOT NULL, so two cards rendered the
//       same number with different labels). Same surface on /admin/dashboard + /portal.
//   (3) Call Summary CASH / PRE-CALL card ADDED next to CASH / SHOW. Numerator =
//       cashCollected; denominator = funnelMerged.pre_calls_filled (distinct leads
//       from useFunnelHealth). Rayston May 1-7 ~$1,452/lead.
// usePortalKpis.ts: bug fix at line 265 (setterBookedCalls now reads r?.setter_booked
// not preCalls = pre_calls_made). Portal cashPerPreCall computed inline using
// preCalls (event count) interim — divergence with ops dashboard (distinct-lead
// denominator) flagged P13.
// Frontend semantic shift requires cache eviction so dashboards don't display the
// old 88-94 SETTER BOOKED number from the last cached payload.

// v113-calendar-chrono-greedy-outcome-binding (2026-05-08)
// ────────────────────────────────────────────────────────
// useCalendarEnrichment.ts switched from `outcome.callStartTime` field-match
// to chrono-greedy queue: per-lead, push call_booked, remove on call_cancelled,
// pop oldest unmatched on sales_call_outcome → bind. Outcome binding key
// changed from lead_id → transaction_id. Cache shape changed:
//   v2: { preCallLeadIds, outcomes: Array<[lead_id, OutcomeRecord[]]> }
//   v3: { preCallLeadIds, bindings: Array<[txn_id, OutcomeRecord]> }
// Visible chip behavior shift: chips with mismatched callStartTime now
// receive their outcome binding (e.g. Lam Ming Wen May 7 10:00 ← CPP),
// missing-outcomes warning panel shrinks, doctrine-correct rebook flagging
// preserved (new booking after outcome adds a fresh queue entry the prior
// outcome already retired). Matches Mako's May 8 ruling: don't punish
// closers for impromptu off-calendar calls with leftover-slot admin.
// Bumping forces eviction of cached useCalendarEnrichment payloads so the
// Missing Outcomes sidebar lights up correctly on next refresh.
// v114-calendar-chrono-greedy-batched-leadid-fetch (2026-05-08, hotfix on top of v113)
// ─────────────────────────────────────────────────────────────────────────
// v113 introduced the chrono-greedy queue but fetched the bindings query as
// a single `.in('lead_id', [...224 UUIDs])` call. The resulting query string
// (~9KB) exceeded a proxy URL limit, the request silently returned an error,
// `bindingsResult.data || []` resolved to [], the queue saw zero outcomes for
// every lead, and the Missing Outcomes warning panel jumped 6 → 63 (every
// past chip looked uncovered). v114 batches lead_ids in chunks of 100 and
// paginates each batch up to PGRST_MAX_ROWS=1000. Bumping evicts any cache
// payloads written during the broken v113 window so refreshed clients
// recompute bindings against the full row set.
// v115-calendar-terminal-drain (2026-05-08, on top of v114)
// ─────────────────────────────────────────────────────────
// Outcome binding rule extended: terminal outcomes (paid_in_full,
// closed_payment_plan, lost, abandoned_payment_plan) now drain the lead's
// entire queue — every still-unmatched booking gets bound to the terminal
// outcome. Non-terminal outcomes (no_show, rescheduled, unique,
// restored_from_abandon, paying_installment) still pop one slot at a time.
// Captures Mako's May 8 doctrine: "lost" closes the lead, leftover calendar
// slots are noise the closer shouldn't have to also cancel. Rebook-reopens
// preserved — a NEW booking after a terminal enters the queue fresh.
// Bumping evicts cached useCalendarEnrichment payloads written under v114
// where Brendon's May 2 chip (and similar) were still flagged.

// v116 (May 9, 2026) — TRUE-EMBED Cash Hero migration. Invalidates:
//   - altara_v*_portal_kpis_* (usePortalKpis cache, may have stale shape)
//   - altara_v*_portal_metrics_* (useClientPortalMetrics cash-fields cache)
//   - altara_v*_ops_dm_* (useOperationsAnalytics cache, KPI fields rearranged)
//   - NEW: altara_v*_ops_kpis_* (useOperationsKpis canonical primitive)
//   - NEW: altara_v*_daily_metrics_* (useDailyOrgMetrics canonical primitive)
// CACHE_VERSION auto-increments via __BUILD_COMMIT__ on next deploy.

// v117 (May 11, 2026) — post-call packer formatToUTCMinus8 → formatTimestampUTC fix.
// Backfilled 108 sales_call_outcome rows on Rayston (event_date +8h). Apr 30 → May 10
// UTC, all `google_form/post_call`. Calendar Missing Outcomes panel was reading
// shifted event_date values pre-fix, producing chrono-walk false positives (Brian
// Tong, Guang Rui, Karine Chang, Satish). Backfill changes shape of cached calendar
// enrichment payloads + DM analytics daily buckets + leaderboard daily buckets
// for the Apr 30 → May 10 window. Bump evicts:
//   - altara_v*_calendar_enrichment_* (useCalendarEnrichment)
//   - altara_v*_dm_analytics_* (useDmAnalytics daily buckets)
//   - altara_v*_leaderboard_daily_* (rep daily totals)
//
// v118 (May 11, 2026) — P0.8-Receipts launch. New get_leaderboard_receipts RPC +
// useLeaderboardReceipts hook write to a new altara_v*_lbr_* cache key family
// (separate from useLeaderboard's altara_v*_lb_*). The RPC payload reshapes which
// per-rep receipts list is fetched — bumping forces the page-level useLeaderboard
// reads to re-fetch alongside the new sibling hook so the popover/modal don't
// render stale cell sums against fresh receipt arrays. Bump evicts:
//   - altara_v*_lb_* (useLeaderboard) — keeps cell value + receipts in lockstep
//   - altara_v*_lbr_* (useLeaderboardReceipts) — new key family on first run
// v119 (May 11, 2026) — S29 dismiss-filter sweep across DM Analytics. 9 SQL functions
// + 1 new bulk_dismiss_suffering_conversations RPC + backfill of affected daily metrics.
// Killed/dismissed-without-resurface convs now correctly hidden from hero numbers, V2
// funnel, recent conversations, Engaged/Unengaged surfaces, proof grids, and setter
// performance dashboards in one sweep. Time-aware filter shape adopted as new SSOT:
//   (dismiss_type IS NULL OR (dismiss_type = 'dismissed' AND last_message_at > suffering_dismissed_at))
// Mirrors the Suffering tile semantics (show dismissed convs IF lead messaged after dismissal).
// Affected RPCs: get_dm_analytics_fast, get_engaged_per_setter, get_setter_unengaged_proof,
// refresh_daily_dm_metrics, get_setter_proof, get_setter_proof_rows, refresh_setter_daily_performance,
// get_setter_performance_fast, get_setter_cross_day_metrics. New: bulk_dismiss_suffering_conversations.
// Live verification Rayston Apr 2026: hero total_engaged 3141 → 3138 (-3 = Glen+Carlos+__who1s_),
// total_new_dms_lead_replied 4485 → 4482 (same -3). Pool match invariant hero==bars TRUE.
// Frontend P1: BulkDismiss + BulkKill in ProofGridView action bar (RBAC-gated via two named
// flags on useRBAC). Bump evicts cached proof/performance/dm payloads keyed on prior shape.

// v122 (May 13, 2026) — /admin/transactions org-wide for all in-org roles.
// RLS policy `transactions_tunnel_vision_select` collapsed from 5-branch role-tunnel-vision
// to 2-branch super_admin + EXISTS-on-organization_members. closer/setter/dialer now see
// every rep's transactions in their org (cross-tenant isolation preserved). Frontend flag
// `canSeeAllRepTransactions` in useRBAC.ts gates the userId-pass-through on TransactionLogPage.
// Reps' cached transactions payloads were per-rep filtered; bump evicts so they refetch
// the org-wide shape on next load. MiniTransactions / RepCompareColumn unchanged (pass
// explicit userId). Live verification: closer in Rayston visible SCO 186 → 1095 (5.9×),
// cross-tenant SCO = 0, dialer SCO 0 → 1095.
//
// v123 (May 13, 2026) — get_reply_turn_distribution dismiss filter tightened.
// Closes the S29 (May 11) sweep gap — this was the last live RPC still using the lax
// shape (IS NULL OR = 'dismissed'). Hero "Reply Turns" card numbers diverged from the
// proof modal because modal RPCs (get_setter_proof, get_setter_proof_rows) already used
// the tight time-aware shape from S29. Live verification Rayston Alice May 7-13 PHT:
//   - 0-bucket total: 88 → 63 (-25), lead-last: 28 → 4 (-24). Drift was dismissed-and-
//     quiet conversations sitting in the 0-bucket falsely flagged as "owes reply."
//   - 1-bucket: 286→284, 2-bucket: 76→75. 19+: 10→10 (no drift on high-turn buckets —
//     reps don't Dismiss/Kill 19-turn convos).
//   - Total Alice: 590 → 562, matches proof modal exactly.
// Filter shape (now SSOT across ALL DM Analytics RPCs):
//   (dismiss_type IS NULL OR (dismiss_type = 'dismissed' AND last_message_at > suffering_dismissed_at))
// Effect: dismissed-and-quiet excluded; dismissed-but-lead-came-back included; dead
// (Kill button) excluded permanently. Bump evicts cached hero/distribution payloads:
//   - altara_v*_dm_reply_turn_* (useReplyTurnDistributionAll SWR keys)
//   - altara_v*_setter_perf_* (useSetterPerformance reply-turn merge)
//
// v124 (May 14, 2026) — S31 — Reply Turn Distribution lifetime/window toggle. New RPC
//        get_setter_per_lead_reply_turns_window returns per-conversation
//        in-window reply turn count + lifetime sidecar. useReplyTurnDistributionAll
//        cache key now includes windowMode — two separate cache entries
//        (one per mode) means flipping the toggle never reads stale
//        cross-mode data. Clients holding altara_dm_reply_turn_v1 blobs
//        from before this change MUST refetch — evictStaleCaches() handles
//        on next page load.
//
// v125 (May 14, 2026 PM) — Sequence A — leaderboard orphan_precalls staleness fix.
//        Backfilled recalculate_daily_leaderboard for May 2 → today × all active orgs
//        (76 daily_leaderboard rows refreshed for May 2-14). 24 of 26 Rayston rows
//        had orphan_precalls = 0 pre-backfill because NO cron was scheduled to run
//        recalculate_daily_leaderboard since the May 1-2 AFK seed. lb.show_rate is a
//        generated column = shows / (shows + no_shows + orphan_precalls) × 100, so
//        stale orphans inflated leaderboard show rates ~10-17pp vs the rep portal
//        canonical (Adrian 79.0% → ~68.7%, Beryl 81.1% → ~63.6% for May 1-14).
//        Also re-rolled rollup_prestige_points + calculate_prestige_rankings for
//        May 1 → today × all orgs so the downstream prestige_points rows reflect
//        corrected show rates. New cron daily-leaderboard-recalc (0 18 UTC = 02:00
//        SG-tz next-day) with rolling 14-day window keeps this from drifting again.
//        Bump evicts cached leaderboard payloads on /admin/leaderboard so reps see
//        canonical numbers on next page load instead of waiting for natural TTL.
//
// v126 (May 14, 2026 PM) — Sequence B — orphan-rule doctrine alignment (both writers).
//        recalculate_daily_leaderboard AND get_rep_portal_data both rewritten to
//        match the /metrics May 13 SSOT orphan rule. Three bugs fixed at the source:
//        (b) ±1 day SCO slack — both functions previously required
//            sco.call_start_time >= pc.call_start_time exactly. Now uses
//            >= pc.call_start_time - INTERVAL '1 day' to absorb closer-form
//            date-picker drift (SCO side remains closer-typed).
//        (c) UNCANCELLED future call_booked — both previously treated any
//            future-dated call_booked as exempting the pre-call from orphan-hood.
//            Now requires NO matching call_cancelled (same lead_id + same
//            call_start_time). Cancelled bookings no longer protect pre-calls.
//        (d) get_rep_portal_data ONLY — replaced SELECT DISTINCT op.lead_id with
//            DISTINCT ON (op.precall_id). Multi-role pre-call pairs (setter + dialer
//            on the same lead) now both count as separate orphans per /metrics rule (d).
//            Also removed the pre-Mako-May-13 unauthorized pc.status='unique' filter
//            on pre-calls (only SCO rows are exempted by status='unique', not PCs).
//        Per /metrics rule 472: Feb 13 → today daily_leaderboard backfill ran for
//        all orgs (Feb 13 is the precall_outcome_matches freeze date — earliest
//        possible affected window). Prestige re-rolled Feb-May × all orgs.
//        Verification (May 1-14 Rayston): Adrian lb=64.8% / rp=64.8% (0.0pp diff),
//        Beryl lb=58.3% / rp=58.3% (0.0pp diff). Both surfaces now read identical
//        doctrine-compliant orphan counts. Bump evicts cached leaderboard + rep
//        portal payloads on next page load.
//
// v127 (May 14, 2026 PM) — S31 Step 5.7 — Reply Turn Distribution drill-down
//        Window-mode threading. New 9th arg `p_window_mode TEXT DEFAULT 'lifetime'`
//        on get_setter_proof_rows (DROP+CREATE because Postgres won't accept arg
//        list changes via CREATE OR REPLACE). In Window mode, the
//        reply_turn_distribution branch re-aggregates per-conversation reply turns
//        via LAG-over-PARTITION-BY inside the date range (mirrors
//        get_setter_per_lead_reply_turns_window canonical pattern). Bucket
//        assignment now uses in-window count; JSONB row carries new
//        lifetime_reply_turns sidecar. Frontend: SetterProofModal threads
//        windowMode into the RPC call + per-bucket cache key, ProofGridView
//        renders new `lifetime_label` muted badge after the primary turn-count
//        badge. Closes the user-visible "bar says 1 but drill shows 3" bug
//        (Mako screenshots May 14 — A had 12 lifetime turns but only 1 in-window
//        turn; was bucketed at 11_12 in window mode, now correctly at 1).
//        Bump evicts cached `altara_*_proof_*` payloads — any session that
//        opened the modal pre-fix has the wrong bucket assignment cached.
//
// v128 (May 15, 2026) — S51 — Operations Dashboard /metrics alignment pass.
//        (1) get_operations_kpis.sql: orphan rule updated to May 13, 2026 lock —
//        ±1 day SCO back-tolerance on gate (b) absorbs closer-form date-picker drift;
//        gate (c) anchor changed from pc.call_start_time to p_end_date with
//        cancel-match on future call_booked (cancelled rebooks no longer exempt).
//        Live verified Rayston Apr 2026: orphan_pre_calls 56→41, orphan_rate
//        28.1%→18.2%. New RPC field `calls_booked_settled` = right-censored
//        pre-call count for cashPerCall denominator (excludes future-scheduled).
//        (2) Hook: cashPerCallBooked denominator switched to calls_booked_settled;
//        scoNoShows + orphanPreCalls now exposed separately on kpis so the UI can
//        split the No-Shows tile into "{N} call · {M} orphan" sub-line. Dead
//        funnelQuality (renamed from funnelEffectiveness per R55) + qualityOfProspects fields stripped from DashboardKPIs
//        (PARKED per /metrics SSOT, never displayed since S35).
//        (3) UI: pipelineOutreach "Calls Booked" + "Pre-Calls Made" collapsed into
//        single "Pre-Calls" tile (distinct-lead count); No-Shows tile gains split
//        sub-line; funnel-grid "Cancellations" now uses gated kpis.cancellations
//        (matches Call Summary). SalesPerformanceCard: "Cancelled" → "Cancellations",
//        new No-Shows tile with split sub-line. stageMetrics "Cash/Call" → "Cash / Show".
//        (4) Portal: usePortalKpis exposes scoNoShows + orphanPreCalls (matches
//        SalesPerformanceCard's new SalesPerformanceKpis interface).
//        Bump evicts every `altara_*_ops_kpi_*` + `altara_*_portal_kpis_*` payload —
//        every consumer needs the new RPC shape + new no-show sub-fields on next load.
//
// v129 (May 15, 2026) — S52 — Invoice per-deal commission override.
//        RepSale gains `transaction_id` (used as the override-map key
//        `${rep_id}:${transaction_id}`). Cached `altara_*_inv_rep_*` payloads
//        predating this have sales without transaction_id → every override key
//        collapses to `${rep_id}:undefined`, bucketing all a rep's deals
//        together. Bump evicts stale invoice rep arrays so the override map
//        keys resolve per-deal. Prod auto-evicts via build-SHA; this manual
//        bump covers dev where __BUILD_COMMIT__ falls back to "dev".
//
// v130 (May 16, 2026) — S53 — D-02 doctrine cleanup: paying_installment removed
//        from WIN_STATUSES. paying_installment is a recurring payment event, not a
//        deal win — treating it as a win inflated close rates and total-won figures
//        across every dashboard that calls isWin() or WIN_STATUSES.
//        Changes: utils/transactionStatus.ts (WIN_STATUSES tightened, isWin
//        updated); useInvoice.ts substring-match bug fixed; 7 components updated
//        (TimeGridView, MonthView, MiniTransactions, LeadCard, OverallSplitDonut +
//        V2, DailyOutcomesChartV2); usePreCallCompliance.ts installment exclusion
//        made unconditional; 2 SQL migrations pending (WinnersPage RPCs +
//        calculate_invoice.total_won). Every cached altara_*_inv_rep_*,
//        altara_*_ops_kpi_*, altara_*_portal_kpis_*, and altara_*_winners_*
//        payload now recomputes against the tighter doctrine — consumers will see
//        lower wins and lower close rates, which is the correct number.
//        Bump required so stale payloads (which counted installments as wins) are
//        evicted and all dashboards render consistent, doctrine-compliant figures.

// v131 (May 17, 2026) — Stoplights page wired into the app shell.
//        New route /admin/stoplights registered in App.tsx (StoplightsPage lazy
//        import). Added to ALL_PAGES and manager.pages in roleConfig.ts (exec
//        auto-derives via ...ALL_PAGES). Sidebar metadata entry added to
//        SIDEBAR_ITEMS and /admin/stoplights appended to SECTIONED_LAYOUT.management
//        in sidebarItems.ts. No RPC or cache shape changed — bump covers the new
//        route registration so any cached nav/role payloads that reference the old
//        page list evict cleanly on next App boot.

// v132 (May 17, 2026) — Stoplights compute-on-demand rework (D6 reversal).
//        useWeeklyStoplights hook switched from
//        .from('weekly_stoplight_snapshots').select() to
//        .rpc('get_weekly_stoplights', { p_organization_id, p_weeks_back }).
//        weekly_stoplight_snapshots table dropped. rollup_weekly_stoplight function
//        dropped. pg_cron job 'weekly-stoplight-rollup' (jobid=19) unscheduled.
//        New RPC get_weekly_stoplights + private helper _compute_weekly_stoplight_row
//        compute each ISO week live via generate_series + get_operations_kpis.
//        Cold load ~824ms for 52 weeks (Rayston, May 17). SWR 24h TTL absorbs.
//        Cache key shape unchanged (same cacheKey function in hook) but the payload
//        is now computed fresh vs. read from a frozen row — existing altara_stoplights_*
//        keys must evict so clients call the RPC instead of serving frozen snapshots.
//        Mako's doctrine: "the admin dashboard already computes on demand — just do
//        the same." See docs/handoff/[doing]_STOPLIGHTS_PAGE.md post-mortem May 17.

// v133 (May 17, 2026) — Stoplights hotfix: super_admin gate + error display.
//        HOTFIX: get_weekly_stoplights blocked Mako (is_super_admin=true) because
//        admin@dynamisrev.com is only in organization_members for Dynamis org, not
//        Rayston. RPC membership gate had no is_super_admin short-circuit.
//        Applied migration 2026-05-17_stoplights_hotfix_super_admin_gate.sql —
//        gate now checks is_super_admin first (canonical pattern from Apr 21 migration).
//        Also hardened useWeeklyStoplights error conversion: plain-object throws
//        (e.g. from unexpected non-PostgrestError paths) now surface .message/.details
//        instead of falling through to String(e) → "[object Object]".
//        Cache bump evicts any stale altara_stoplights_* keys so the fixed RPC is
//        called fresh on next load.
// v134 (May 17, 2026) — Invoice rep card: installment payments restored to
//        sales list + cash + commission, but NOT to deals_closed / calls / shows.
//        D-02 cleanup (v130) used isWin() as a single gate inside useInvoice.ts,
//        which dropped installment rows from EVERY rep-card field (sales list,
//        Total Revenue, Commission Due, AND deals_closed). Doctrine says wins =
//        PIF+CPP only (correct), but commission still applies to installment cash
//        (the rep collected the money). Split the gate: isWin → new-deal branch
//        (deals_closed + calls/shows + sale/cash/commission). isInstallment →
//        recurring-cash branch (sale/cash/commission only). RepInvoicesCarousel
//        setter "Closed" stat also switched from rep.sales.length →
//        rep.deals_closed so the count stays wins-only after installments rejoin
//        sales[]. Bump evicts every cached `altara_*_inv_rep_*` payload missing
//        the installment rows.
// v135 (2026-05-18) — Rep portal engine metrics: get_rep_portal_data gained five
//                     new columns (period_pif, period_cpp,
//                     period_distinct_leads_booked, period_raw_bookings,
//                     period_revenue_closed). Cached rep-portal payloads from before
//                     this deploy lack the new fields — consumers reading those keys
//                     would see undefined/0 for every new metric tile. Bump evicts
//                     all altara_*_rep_portal_* keys on next App boot so the RPC
//                     is re-fetched with the full five-field shape.
// v136 (2026-05-18) — Setter attribution data fix (no shape change). 4 Rayston
//                     sales_call_outcome rows (Morris C, Jonathan Mah Weng Fye,
//                     Akech Jok, Pravin Raj) had setter_id = NULL because their
//                     sales_pre_call was FK-deferred by trg_correct_pc_call_start_time
//                     and replayed AFTER the close, so trg_resolve_setter_attribution
//                     fired with precall_count=0. Touch-UPDATE re-resolved each
//                     (Samantha/Alyssa, role dialer); daily_leaderboard recalculated
//                     for Rayston 2026-05-15 + 2026-05-16. Cached transactions_readable,
//                     leaderboard, and setter-scorecard payloads still hold the stale
//                     setter=NULL / pre-fix leaderboard rows — bump evicts altara_*
//                     keys so corrected attribution shows on next App boot.
// v137 (2026-05-18) — Reply Turns scorecard card right-click lifetime/window
//                     flip. useReplyTurnDistributionAll's SetterReplyTurnRow
//                     gained an avgTurns field (exact in-window mean, drives the
//                     inline card's "avg:" in window mode). Cached
//                     altara_dm_reply_turn_v1 payloads from before this deploy
//                     lack avgTurns — a stale window row would render avg as
//                     NaN. The version gate rejects old-shape entries; bump
//                     evicts them so the first right-click refetches the full
//                     {avgTurns, buckets, totalCount} shape.
// v138 (2026-05-19) — daily_org_metrics now attributes call-type events
//                     (sales_call_outcome etc.) by call_start_time in ORG
//                     timezone, was event_date in UTC. refresh_daily_org_metrics
//                     + trigger_refresh_daily_metrics rewritten and the full
//                     table backfilled (migration 2026-05-19_daily_org_metrics_
//                     call_start_time_attribution.sql). The Cash & Revenue chart
//                     (useDailyOrgMetrics → FinancialGraph) per-day cash/revenue
//                     changed for historical days so it reconciles with the
//                     get_operations_kpis card. Before the fix the chart tooltip
//                     and the single-day CASH COLLECTED card disagreed (Rayston
//                     2026-05-18: chart 41,049 vs card 36,049 — one mis-logged
//                     $5k deal). Cached altara_*_daily_metrics_* payloads hold
//                     the pre-backfill day distribution; bump evicts them so the
//                     corrected per-day values show on next App boot.
// v139 2026-05-20: stoplights weeks now anchor on Sunday (Sun→Sat), not Monday.
//                     get_weekly_stoplights migration shifted v_current_anchor
//                     from date_trunc('week', today) to today - DOW. The cache
//                     key on useWeeklyStoplights includes (count, daysSpan,
//                     strideDays) but NOT the anchor date, so a cached Monday
//                     entry under the same (4, 7, 7) triple would collide with
//                     a Sunday-anchor response — wrong column headers, wrong
//                     numbers per cell. Bump evicts so next App boot reads
//                     Sunday-anchored data fresh.

// v140 2026-05-24: client_tasks SELECT no longer requests the dropped `tags`
//                     column. The column was dropped May 15 via
//                     2026-05-14_drop_client_tasks_tags_column.sql, but the
//                     SELECT strings in ClientPortalContext + ClientPortalPage
//                     still asked for it — every read returned HTTP 500
//                     (Postgres 42703), frontend silently reset clientTasks to
//                     [], created tasks "vanished" after refresh. Bump evicts
//                     stale empty/broken portal caches so post-fix users get a
//                     fresh load on next App boot. Affected keys: any
//                     altara_*client*portal* entry.

// v141 2026-05-24: exec-only "Permanently Delete" button shipped on
//                     ArchivedView. New SECURITY DEFINER RPC
//                     hard_delete_client_task drops a task + its
//                     client_task_versions audit history atomically; FE button
//                     gated by new useRBAC flag canHardDeleteClientTask
//                     (exec only). Cached archive lists could briefly show a
//                     ghost row after delete until SWR refresh; bump evicts so
//                     post-delete state is fresh.

// v142 2026-05-24: get_forecast_data — pace card excludes today from both
//                     numerator (cash) and denominator (days). Mako rule: an
//                     in-progress day is noisy — partial cash pulls the daily
//                     average down all morning, then snaps back up after EOD
//                     deals land. Now days_elapsed = today - month_start (no
//                     +1) and the cash_so_far filter is `< v_today` instead of
//                     `<= v_today`. days_remaining gets a +1 so the identity
//                     elapsed + remaining = days_in_month is preserved. May 24
//                     Rayston live diff: pace $572,248 → $587,020 (+$14,772),
//                     because today's $7,500 partial dragged the prior 23-day
//                     daily average ($18,936/day) down to a 24-day blended
//                     ($18,460/day). Migration:
//                     sql/migrations/2026-05-24_pace_exclude_today.sql,
//                     canonical synced at sql/analytics/get_forecast_data.sql.
//                     daily_cumulative chart intentionally left including
//                     today — that's progress visualization, not projection.
// v143-calendar-pagination (2026-05-25) — useCalendarData + the CalendarPage
//                     adjacent-range prefetch now paginate the events fetch with
//                     .range() + an .order("id") tiebreaker instead of a single
//                     .limit(1000). The hard cap silently truncated busy months
//                     (Rayston May 2026 grid = 1378 call rows, ascending order, so
//                     every date past row 1000 — the 19th onward — was dropped from
//                     the Month view even though the rows exist in Postgres). Bump
//                     evicts the cached truncated `cal_events_*` blobs so clients
//                     refetch the full paginated set on next load. No SQL/RPC change.
// v144-invoice-self-set (2026-05-31) — the cached rep-invoice blob
//                     (`rep_{org}_{start}_{end}`) now carries a third field,
//                     `selfSetTotal`: cash on high-ticket SCO rows with a closer
//                     but no setter/dialer (setter_id NULL = no pre-call backed the
//                     deal). It powers the new "By Setter + Dialer" reconciliation
//                     row, where self-set is MEASURED independently (not derived as
//                     closer − setter). Stale caches lack the field → it reads 0 and
//                     the setter row shows a false amber gap until TTL; bump evicts so
//                     clients refetch with the field populated. Prod auto-evicts via
//                     build-SHA; this manual bump covers dev (__BUILD_COMMIT__→"dev").
//                     No SQL/RPC change — the field is computed in useInvoice from the
//                     same transactions already fetched.
// v145-invoice-self-set-12pct (2026-05-31) — self-set deals now pay the closer
//                     12% (org_members.self_set_commission_base) instead of the
//                     standard 10%. The cached rep blob's RepSale gains is_self_set
//                     and RepInvoiceData gains self_set_commission_rate. A stale
//                     cache lacks is_self_set → reads falsy → self-set deals
//                     silently revert to 10% AND lose their tag/split. Bump evicts
//                     so clients refetch with the flags + rate populated. Prod
//                     auto-evicts via build-SHA; manual bump covers dev. No SQL
//                     shape change beyond the organization_members column add
//                     (migration 2026-05-31_self_set_commission_base.sql).
// v146-invoice-team-role-payout (Jun 2 2026): RepInvoiceData gained `team_role` (true
//                     org_members.role) for the partner-payout PDF's closer/dialer/setter
//                     grouping. Stale cached inv_rep_* blobs lack it → payoutGroupOf falls
//                     back to effective role and dialers silently re-collapse into "setter"
//                     for the 5-min TTL window. Bump evicts. (Payout/bank details are NOT
//                     cached — fetched on demand at PDF-gen time — so no PII rides the cache.)
// v147-invoice-client-earnings (Jun 2 2026): the cached inv_rep_* blob gained
//                     `clientEarnings` (org-wide cash + revenue split high-ticket SCO /
//                     installments / low-ticket) for the Partner Payout PDF earnings summary.
//                     Stale cache lacks it → reads null → the earnings block silently doesn't
//                     render for the 5-min TTL window. Bump evicts so it refetches with the
//                     buckets populated. No SQL shape change — useInvoice just adds
//                     revenue_closed to its existing SCO + low-ticket SELECTs.
// v148-invoice-dialer-base-pay (Jun 2 2026): RepInvoiceData gained `base_pay_monthly`
//                     (org_members.base_pay_monthly, new column, default 0). Featured on
//                     the Partner Payout as base_pay_monthly / 2 per half-period invoice.
//                     Stale cached inv_rep_* blobs lack it → reads 0 → base pay silently
//                     missing for the 5-min TTL window. Bump evicts. DB migration
//                     2026-06-02_dialer_base_pay.sql (additive, already applied to prod).
// (Jun 2 2026 — Session 14 NOTE): a v149 reschedule-supersession bump was added then
//                     REVERTED in the same session — /metrics says `shows` includes
//                     rescheduled with NO supersession (parked rule). That left cacheVersion
//                     at v148; the bump number was then reused below for the real change.
// v149-rep-portal-self-set-commission (Jun 2 2026): get_rep_portal_data now pays a CLOSER's
//                     setter-less deals (setter_id IS NULL) the 12% self_set_commission_base
//                     instead of 10%, per-row — so commission_this_month/year/lifetime + the
//                     rate badge change VALUE for any closer with self-set deals (RETURN shape
//                     identical → no TS error on stale cache). RepPortalPage also now HIDES the
//                     commission line for dialers (always 0, salaried). Bump evicts stale
//                     portal_rep_* blobs. Migration 2026_06_02_rep_portal_self_set_commission_12pct.
// v150-dialer-setter-mirror-and-antijoin (Jun 2 2026 — Session 25, Stage A): the dialer
//                     leaderboard formula now mirrors the setter (CPC 40 / Show Rate 30 /
//                     Booked 30) instead of the old CPC/Dials/CTB/Show-Rate blend — daily_dials
//                     and connect_to_book_rate were never populated, so two of four terms were
//                     always 0. Dialer CPC switched from cash÷shows to cash÷calls_booked. P1.5
//                     anti-join: a dialer's calls_booked now excludes leads any setter also
//                     pre-called (solo bookings only). Dialer min-volume gate: ≥10 dials → ≥5
//                     calls booked. Dialer columns Dials/CTB → Booked. Cached leaderboard blobs
//                     hold the old dialer rank_score + Dials/CTB shape. Migration
//                     dialer_setter_mirror_antijoin_v1. (Daily data re-rolls in Stage C.)
// v151-leaderboard-ranks-by-points (Jun 2 2026 — Session 25, Stage B): the leaderboard now
//                     SORTS by points-in-window (§3.7 winner rule) instead of the rank_score
//                     KPI composite — range ranker, daily-company view, and the per-row ScoreBar
//                     all track points_earned; rank_score stays computed for the tiebreak + hover.
//                     This is a CLIENT-SIDE sort/display change only — no API response shape
//                     changed — so the bump is belt-and-suspenders (evicts any leaderboard blob
//                     whose cached row order a memo might reuse). No SQL migration.
// v152-rep-portal-calls-booked-canonical (Jun 2 2026): get_rep_portal_data renamed the dead
//                     period_leads return field to period_calls_booked and now fills it from a
//                     standalone precall_booked CTE = COUNT(DISTINCT lead_id) of the rep's
//                     in-period pre-calls with the setter>dialer anti-join (dialer assist on a
//                     setter-handled lead is dropped). The setter/dialer "Calls Set" tile is
//                     relabeled "Calls Booked" and now reads periodCallsBooked instead of
//                     periodCalls. BEFORE: the tile showed periodCalls (= SCO shows + no_shows),
//                     so dialers read finished-call outcomes (Alyssa 50 / Samantha 75) instead of
//                     booked calls. The old pre-call field was COUNT(DISTINCT t.id) joined to
//                     ghl_conversations → cartesian-zeroed for dialers. AFTER: canonical per
//                     /metrics "Setter calls_booked" P12 + "Dialer calls_booked" May 14 rule
//                     (Alyssa 70 / Samantha 108). Closers keep "Calls" = periodCalls. Response
//                     shape changed (column rename) → cache must evict.
// v153-tz-date-boundary-fix (Jun 2 2026): client-side timezone date-boundary fix. Several hooks
//                     built query bounds + localStorage cache keys from UTC date strings
//                     (.toISOString().split/slice) or Date.UTC(localGetters) instead of the
//                     org-local day, so for UTC+8 orgs (Rayston Asia/Singapore) a range dropped
//                     its last day (or the start day lost its first 8h). Swapped to lib/timezone
//                     helpers: date-column bounds + cache keys via toOrgDateKey (useLeaderboard,
//                     useWinnersAnalytics params+key, useDMAnalytics key, TransactionContext key);
//                     timestamptz bounds via toOrgStartISO/EndISO (usePortalKpis, MiniSetterStats,
//                     useSetterLeadTimelines); stored-timestamp day buckets via
//                     timestampToOrgDateKey (DailyOutcomesChartV2, TransactionLog). The
//                     leaderboard / winners / dm / transaction-context cache-key SHAPES changed,
//                     so stale altara_* blobs hold the wrong window — this bump evicts them.
// v154-rep-portal-cash-by-call-date (Jun 3 2026): get_rep_portal_data now anchors the DISPLAYED
//                     "Cash Collected" (cash_this_month / cash_this_year) on CALL DATE
//                     (COALESCE(call_start_time, event_date) for call-type events, event_date for
//                     the rest — the canonical daily_org_metrics:172 / leaderboard rule), via two
//                     new commission_calc aggregates (c_cash_collected_month/year). BEFORE: hero
//                     summed by event_date (filed/logged date), so a late-April call typed up in
//                     May counted in May → Adrian read $301,207 vs the leaderboard's $290,207.
//                     AFTER: hero = $290,207, matches /admin/leaderboard + /operations chart.
//                     COMMISSION (commission_this_month/year, the rate badge, cash_lifetime) is
//                     UNCHANGED — deliberately stays event_date-anchored (filed date) per Mako, so
//                     commission timing on cross-month deals doesn't move. Response value of two
//                     columns changed → stale altara_* blobs hold the old $301k hero, bump evicts.
// v155-may-prestige-winners-committed (Jun 3 2026): committed the May 2026 prestige winners for
//                     Rayston via process_monthly_prestige(p_preview=false) — closer Adrian, dialer
//                     Samantha, setter Alice all rank_up 0→1 (Prestige 1). Required a prior re-roll
//                     of May (DELETE prestige_points + per-day recalc/evaluate/rollup + rankings) to
//                     flip the corrupt closer ranking from Beryl→Adrian (stale cron-order bug, see
//                     handoff P0-WINNER-INTEGRITY), plus seeding level-0 prestige_ranks rows for reps
//                     who'd never been ranked (Samantha/Norman/etc — the month-end engine loops
//                     prestige_ranks and silently skipped them). get_rep_portal_data.prestige_level
//                     (= prestige_ranks.current_level) changed 0→1 for 3 reps → stale altara_* portal
//                     blobs hold level 0 (gray "—" badge) → this bump evicts so the P1 emblem renders.
// v156-prestige-may-winners-only (Jun 3 2026): per Mako, ONLY the May winners carry prestige —
//                     cleared the two March holdovers (closer Beryl, setter Patil) from current_level 1
//                     back to 0 (+ reset highest/last_win/consec, deleted their May buffer_used history
//                     rows). They were holding P1 via the grace buffer (won March, didn't win May). Now
//                     prestige_ranks.current_level=1 only for Adrian/Samantha/Alice. Their portal blobs
//                     cached level 1 under v155 → this bump evicts so Beryl/Patil drop back to the gray
//                     badge. Reversible via prestige_ranks/history_snapshot_pre_crown_20260603.
// v157-portal-g2-g3 (Jun 4 2026): portal G2 frontend — is_draft column on client_tasks now in
//                     refetchTasks select + normalizePortalItems (KanbanCard draft shade/pill).
//                     Query shapes changed: is_draft on tasks read (affects cached PortalItem[]).
// v158-speed-to-lead-card (Jun 10 2026): S40 Speed to Lead metric added to DM Analytics setter
//                     cards. New hook useSpeedToLead writes localStorage keys with prefix
//                     `altara_dm_speed_to_lead_v1` — a new payload shape (median_minutes,
//                     pct_under_5min, lead_count from get_setter_speed_to_lead RPC). Any cached
//                     entry under the old key prefix is now stale; bump evicts so the new card
//                     fetches fresh data on first load. Display-only v1 (non-clickable, no proof
//                     modal). Companion entry added to constants/metricMeta.ts (speedToLead key).
// v159-pending-close (Jun 10 2026): Pending Close feature (CALENDAR_PAGE_FIXES). New
//                     transactions.status value 'pending_close' joins the shows/calls-taken
//                     allowlists in 9 RPCs (get_operations_kpis JSON gains a `pending_close`
//                     raw counter; get_funnel_metrics/get_client_portal_metrics/rep cards/
//                     rep portal/receipts/recalc/daily_org/compliance lists widened).
//                     useCalendarEnrichment's bindings select now also fetches custom_data +
//                     created_at and its cached binding entries carry an `openPending` object
//                     ({txnId, expectedAmount, createdAt}) — old cached enrichment blobs and
//                     ops-KPI blobs lack the new fields, so chips would miss the ⏳ Pending
//                     pill and shows would undercount until eviction. Filed via NEW
//                     file_pending_close RPC; converted via convert_pending_close (GUC-guarded).
// v160-speed-to-lead-v2 (Jun 11 2026): Speed to Lead redefined to v2 — clock now starts at the
//                     lead's FIRST-EVER message, bot-first (ManyChat-greeted) conversations
//                     INCLUDED (Mako ruling Jun 10). get_setter_speed_to_lead dropped the
//                     first_sender='lead' fence and switched its coarse prefilter from
//                     first_msg_date to first_lead_msg_date (greeting-date drift silently
//                     excluded 8.6% of bot-first leads). Same RPC name + payload shape, changed
//                     SEMANTICS — cached `altara_dm_speed_to_lead_v1` payloads hold v1 numbers
//                     (~4.4x lower new_leads); bump evicts so cards show v2 counts. Companion
//                     metricMeta.speedToLead formula/columns/filterDescription rewrite.
// v161-transaction-context-pagination (Jun 11 2026): TransactionContext.tsx fetch
//                     paginated via .range() loop (PAGE_SIZE 1000, id tiebreaker on the
//                     event_date DESC order). Pre-fix the single unpaginated request was
//                     silently capped at PGRST_MAX_ROWS=1000 — every 2026 month exceeds
//                     1,000 rows for the 5 fetched event types (Jun 11 audit: default 30d
//                     Rayston window = 2,632 rows), so any range ≳3 weeks dropped its
//                     OLDEST rows and every KPI/Call Summary value computed from this
//                     context undercounted while RPC-fed cards stayed correct (bug #10
//                     two-source divergence, bug #15 silent cap). Same payload shape,
//                     MORE rows — cached altara_*_transactions_* blobs hold truncated
//                     windows; bump evicts so clients refetch complete data. Companion
//                     fix same session: usePreCallCompliance q3.5 (call_cancelled
//                     enrichment) wrapped in fetchAllPages. Jun 11 audit item 1+5.
// v162-speed-to-lead-key-v2 (Jun 12 2026): useSpeedToLead cache key switched from raw
//                     toISOString() instants to calendar date keys (toOrgDateKey start_end)
//                     and the RPC window normalized to org-tz day boundaries via
//                     toOrgStartISO/toOrgEndISO (was raw startISO + browser-local
//                     setHours(23,59,59,999) end). Same payload shape, but (1) old raw-ISO
//                     altara_dm_speed_to_lead_v1 entries are unreachable under the new
//                     key scheme and would linger as dead weight, and (2) the query window
//                     changes for any browser whose tz differs from the org tz (calc change
//                     per Bug #11). Enables the login-prefetch warmSpeedToLead — prefetch
//                     and DateRangePicker construct Dates differently, raw-ISO keys never
//                     matched (Mar 22 prefetch-key class), so warming was impossible.
//                     RT-LATENCY-3 Step 5 (D1/D2/D9).
// v163-stl-first-touch-columns (Jun 12 2026): RT-LATENCY-4 — speed-to-lead first-touch
//                     instants persisted as trigger-maintained stl_first_lead_at /
//                     stl_first_human_reply_at columns on ghl_conversations; the card RPC
//                     + both proof RPC speed branches now read the columns instead of
//                     per-convo LATERALs over ghl_messages (candidates path 58K→6K buffers,
//                     1,015ms→19ms; cold-timeout class killed). Numbers can change by ±1
//                     on some windows: the old coarse first_lead_msg_date ±1d prefilter
//                     was LOSSY — it dropped leads whose first lead message of ANY type
//                     (story reaction, empty text) predates their first QUALIFYING IG
//                     message by >1 day (parity gate caught 2 such leads: Edith/Alice W1,
//                     Kevin Wee/Patil W3 — both now correctly counted per the v2
//                     definition). Intentional numeric change per Bug #11 → bump evicts
//                     cached altara_dm_speed_to_lead_v1 + proof payloads holding the
//                     undercounted values.
// v164 (2026-06-12): cross_day_hydrated boolean added to SetterMetrics (RT-LATENCY-5 Phase A).
//                     New field in cached rows → shape change → old rows must be evicted
//                     so skeletons don't persist on warm cache after upgrade. Field is
//                     non-optional by design (typecheck completeness gate). Old rows
//                     (cross_day_hydrated=undefined) would be treated as falsy → permanent
//                     skeleton — bump evicts them so fresh rows with true/false are written.
// v165-rep-portal-orphan-antijoin (Jun 14 2026): get_rep_portal_data now applies the
//                     setter>dialer anti-join to the DIALER orphan-pre-call count
//                     (orphan_count_for_rep), matching what precall_booked (CALLS BOOKED) and
//                     the resolve_setter_attribution trigger (shows/no_shows) already do. Before:
//                     a dialer's show-rate denominator counted orphans on setter-owned leads the
//                     dialer doesn't even get CALLS BOOKED credit for → show rate deflated ~11-13pp
//                     and the funnel sum (shows+no_shows+orphans) could EXCEED calls_booked
//                     (Samantha Jun 1-14: 58 vs 52, impossible). Live deltas: Alyssa orphans 12→7
//                     show 50.0→60.9%, Samantha orphans 26→15 show 53.4→66.7%. Also flipped CALLS
//                     BOOKED (precall_booked) from event_date to call_start_time anchoring so it
//                     buckets like shows/orphans (Alyssa 35→26, Samantha 52→48). Setters/closers
//                     unchanged. RPC redeployed live. /precall + /metrics compliance fix.
// v166-suffering-teardown-wave2 (Jun 15 2026): removed the user-facing "suffering" METRIC/list from
//                     DM Analytics — inline scorecard list, SufferingGridModal, suffering prefetch
//                     warm + cache (get/setSufferingCache, SUFFERING_TTL, invalidateSufferingCache),
//                     and the "suffering" avg_reply_time proof bucket. The dismiss/dead CAPABILITY is
//                     KEPT and repointed from bulk_dismiss_suffering_conversations to the live twin
//                     bulk_dismiss_conversations (BulkDismiss, CardActions, ConversationViewModal).
//                     Page render shape + cache-key shape changed → evict stale altara_dm_* (incl.
//                     orphaned suffering_* keys). No DB drops yet — Wave 3 is gated on this shipping
//                     to prod.
// v167-cancel-eventdate-backfill (Jun 16 2026): backfilled event_date on 55 manual-cancel
//                     call_cancelled rows (Rayston, clean-era Feb+) from the placeholder
//                     call_start_time (the slot) to created_at (the true filing time). These
//                     rows were written by the N8N manual-cancel workflow (kgFxm4OOoSAaH6Q0),
//                     whose Format Data node copied call_start_time into event_date — which
//                     corrupted the Flake Reschedules predicate (it keys a ±30min window on
//                     event_date). Source fixed the same session: the node now stamps
//                     event_date=nowUTC (the cancel-action time). May flake false-positives
//                     28→0 after the backfill (event_date_flakes==created_at_flakes).
//                     daily_org_metrics proven neutral (cancels bucket by call_start_time, not
//                     event_date — substantive EXCEPT diff 0 rows). Calendar reads event_date
//                     on cancels only via the flake toggle → evict stale altara_calendar_* so
//                     it reads the corrected event_date.
// v168-show-confirmation (Jun 18 2026): Dialer Show-Confirmation feature adds a `confirmations`
//                     field to CachedEnrichment (the calendar enrichment cache shape) — booking
//                     id → ShowConfirmation, fetched from the new lead_show_confirmations table.
//                     Old enrichment entries lack the field; the enrich key embeds CACHE_VERSION
//                     so this global bump evicts them (the in-hook `?? []` is just belt-and-braces).
// v169-stoplights-report-grade (Jun 18 2026): Stoplights Wave 16 — _compute_weekly_stoplight_row
//                     payload gained 4 typeform_* keys, orphan_rate switched to the report-grade
//                     denominator (shows+no_shows+orphan_pre_calls), and per-rep calls_booked is now
//                     the setter-wins deduped count. Cached useWeeklyStoplights entries hold the old
//                     shape/values → evict.
// v170-stoplights-calls-section (Jun 19 2026): Stoplights Wave 17 — org CALLS section rework.
//                     _compute_weekly_stoplight_row payload gained calls_booked_raw (Calendly
//                     call_booked count) + dialer_booked (distinct dialer-OWNED leads, setter-wins
//                     anti-join). Frontend dropped the "Calls Booked (Settled)" + "Closer Booked"
//                     display rows; new CALLS order is Calls Booked (Raw) → Calls Booked → Setter
//                     Booked → Dialer Booked. Cached useWeeklyStoplights entries lack the 2 new
//                     keys → evict.
// v171-stoplights-median-baseline (Jun 19 2026): Stoplights coloring reworked from WoW
//                     (left-neighbor) to a trailing-N-day MEDIAN baseline. Each row gets a new
//                     "MEDIAN Nd" first data column; every period cell is green/red by above/below
//                     that median (binary, no flat band; polarity via goodDirection). New page
//                     control: Median window selector (14 / 30 days, default 30). Median excludes
//                     the partial current period + nulls; computed at display granularity over the
//                     trailing window, independent of Lookback. Hook now fetches
//                     max(lookback, ceil(medianDays/strideDays)+1) snapshots so the window is always
//                     covered. No RPC/payload shape change — but fetchCount changes the cache key
//                     and the render contract changed, so evict to be safe.
// v172-rep-portal-prev-period (Jun 19 2026): Rep Portal — get_rep_portal_data gained 15 prev_*
//                     columns (previous equal-length period for every metric) + a new
//                     get_rep_portal_calls_booked_series RPC. Cached useRepPortalData entries hold
//                     the old shape (no prev_* fields) → the hook maps them to 0, which would show
//                     full-value deltas (current − 0) until refresh. Bump evicts so deltas compute
//                     against real prev-period numbers.
// v173-provisional-closerate (Jun 19 2026): close_rate now counts pending_close as a PROVISIONAL
//                     win in the NUMERATOR ONLY — close_rate = (wins + pending_closes) / shows —
//                     across get_operations_kpis (TS), get_rep_portal_data, get_client_portal_metrics,
//                     get_funnel_metrics, get_portal_rep_cards, get_exec_dashboard_data, and the
//                     daily_leaderboard + daily_org_metrics generated close_rate columns. Cached RPC
//                     responses hold the old (PIF+CPP)/shows close_rate → evict so everyone sees the
//                     provisional number. wins/deals/AOV/cash unchanged (stay PIF+CPP).
// v174-orphan-missingrow-fix (Jun 19 2026): recalculate_daily_leaderboard STEP 3.5 (setter/dialer
//                     calls_booked + orphan_precalls writer) was a bare UPDATE — it dropped both
//                     values on days a rep had pre-calls but ZERO SCO outcomes (no daily_leaderboard
//                     row existed to update). Leaderboard then read a HIGHER show rate than the
//                     correct Rep Portal for every setter/dialer (17/206 Rayston rep-days; up to
//                     +7.7pp). Fixed as an atomic INSERT ... ON CONFLICT DO UPDATE upsert (seed-then-
//                     update can't work — sibling data-modifying CTE shares the UPDATE's snapshot).
//                     Setter/dialer leaderboard show_rate + calls_booked change → evict cached
//                     leaderboard RPC responses. (Backfill of historical rows is a separate gated step.)
// v175-invoice-selfset-commission (Jun 20 2026): useInvoice.applyTxToRep rep-level total_commission
//                     accumulator now dispatches the per-deal rate — a closer SCO row with setter_id
//                     NULL (self-set) accrues self_set_commission_rate (12%) instead of the flat
//                     rep.commission_rate. The displayed money was already correct (carousel
//                     "Commission Due", rep PDF, and payout all recompute via computeRepSaleCommission
//                     from sales[]); the buggy field is the cached repInvoices[].total_commission used
//                     as the carousel SORT key + activity filter, so stale caches show the old
//                     ordering for self-set-heavy closers. Evict so the sort matches the corrected
//                     per-deal commission. (P0-BUG, [altara]_LOST_LEADS_TRIAGE_PAGE.md)
// v176-prestige-kpi-orphanday-backfill (Jun 20 2026): KPI ruling = EXCLUDE orphan-only days.
//                     evaluate_daily_kpi_compliance.active_reps gate changed from
//                     (calls_taken>0 OR calls_booked>0 OR daily_dials>0) to
//                     (calls_taken>0 OR shows>0 OR no_shows>0) — a pre-call-only day (no real
//                     calls) no longer scores 0% show rate → false -1 prestige pt. Then backfilled
//                     recalculate_daily_leaderboard + evaluate_daily_kpi_compliance Feb 13->today
//                     (both orgs) and re-rolled prestige_points for NON-crowned months (Rayston
//                     Feb/Apr/Jun + Dynamis all). Crowned Mar/May left FROZEN (crown-protected;
//                     Mar setter Patil-committed vs Alice-corrected surfaced, NOT changed).
//                     Historical leaderboard orphan_precalls corrected (May +8, Apr +5) so the
//                     leaderboard show rate matches the rep portal. Evict cached LB/KPI responses.
//                     ([altara]_PRESTIGE_SYSTEM_BUILD.md /ham run)
// v177-calendar-calltype-total-upcoming (Jun 21 2026): Calendar sidebar "Call Type" list gains
//                     a TOTAL | UPCOMING segmented control (default TOTAL). Both count DISTINCT
//                     leads by email per type. TOTAL = every lead who reached the stage INCLUDING
//                     cancelled (cancel's type recovered from booked_call_type, stamped in
//                     deduplicateEvents from the dropped booked sibling; ~99.6%). UPCOMING =
//                     leads with an active call still ahead (call_start_time >= now). The type
//                     list is driven by TOTAL so it stays stable across modes (UPCOMING shows 0
//                     for a type with nothing ahead). Bump evicts cached cal_events lacking the
//                     new booked_call_type field so TOTAL counts land immediately.
//                     (Rayston June grid: Discovery TOTAL 206, UPCOMING 3.) ([doing]_CALENDAR_PAGE_FIXES.md)
// v178-triage-claim-clock (Jun 26 2026): get_closer_triage_list dead-clock rewritten to count ONLY
//                     CONTACT events (call_booked, sales_call_outcome, webinar_signup,
//                     typeform_submission) — the old GHL-message / any-activity basis is gone — and
//                     the pool now excludes held leads (a lead with an active self_set_claim: 3d base
//                     + 7 once a call is booked after the claim, terminated only by a PIF/CPP win).
//                     This is the backend for claim-tracking v1 (new RPCs claim_lead, release_claim,
//                     lead_last_contact, get_lead_claim_history). The returned pool content changes
//                     (Rayston 18,546 -> 18,548), so cached get_closer_triage_list responses must
//                     evict. ([altara]_LOST_LEADS_TRIAGE_PAGE.md, migrations 2026-06-26_*)
// v179-triage-server-filters (Jun 27 2026): get_closer_triage_list now applies the Outcome /
//                     Lead-Type / Priority / Days-Dead filters SERVER-SIDE (before the 500-row
//                     LIMIT) instead of the frontend filtering a single top-500 page. Fixes the
//                     "Did Not Show -> 0 leads" bug: those leads (8,032 on Rayston) are tier-6 and
//                     never made the top-500 priority page, so a client-side filter couldn't see
//                     them. The triage cache key now includes a per-filter-combo signature, and
//                     each cached entry's total_count is the FILTERED match count — so the old
//                     filter-less cache entries (and their pool-sized total_count) must evict.
//                     ([altara]_LOST_LEADS_TRIAGE_PAGE.md, migration 2026-06-27_triage_server_side_filters)
// v180-triage-search-journey (Jun 28 2026): get_closer_triage_list gained (a) p_search —
//                     one box matching email/name/phone/country-code server-side; (b) an
//                     `events` jsonb column per lead (last 8 journey events, newest-first) for
//                     the inline mini-journey chips; and the page now requests p_limit=25 (was
//                     500) for speed. New cache key dimension (search) + new response column
//                     (events) → evict v179 triage caches. ([altara]_LOST_LEADS_TRIAGE_PAGE.md,
//                     migration 2026-06-28_triage_search_and_journey)
// v181-invoice-dialer-prorata (Jul 1 2026): dialer base pay prorates by calendar days when
//                     pay_start_date lands mid-period. RepInvoiceData gained 4 fields
//                     (pay_start_date, base_pay_for_period, base_pay_active_days,
//                     base_pay_period_days) and the cached rep blob (`inv_*` key) now carries
//                     them → old cached blobs lack the prorated amount + the roster-filter change
//                     (base_pay_monthly>0 keeps salaried dialers). Prod auto-evicts via build-SHA;
//                     DEV needs this manual bump. (PAGE_invoice_generator…, Working Notes Jul-01;
//                     migration 2026-07-01_dialer_pay_start_prorata)
// v182-dm-hypnogram (Jul 2 2026): DM Analytics setter scorecard replaces the radial
//                     activity clock with an activity hypnogram scoped to the SELECTED
//                     date range (rows = calendar days in range, 8AM->8AM axis). New RPC
//                     get_setter_hypnogram(org, ids, start, end, bin_minutes) returns per
//                     (setter, day) a packed 48-bin message-count array + raw/padded session
//                     minutes + span; new hook useSetterHypnogram caches it in a module-level
//                     Map (not localStorage) so no cached payload shape changed — but the
//                     scorecard render shape did (clock donut → SessionHypnogramChart), so
//                     bump to force dev clients onto the new bundle. Because the band covers
//                     the same window as the card, the header h/day reconciles with the card's
//                     Avg Hours/Day. WorkPatternModal still renders the detailed clock.
//                     Prod auto-evicts via build-SHA; DEV needs this manual bump.
//                     (sql/dm/get_setter_hypnogram.sql, hooks/useSetterHypnogram.ts,
//                     components/dm/SessionHypnogramChart.tsx)
// v183-triage-precompute (Jul 2 2026): Lost Leads / Triage read-path moved off the live
//                     ~2GB transactions re-aggregation. get_closer_triage_list now READS
//                     10 precomputed triage_* columns on `leads` (last_activity/priority_tier/
//                     lead_type/outcome_tag/application_value/last_installment/hold_expires/
//                     setter/closer/signals), kept fresh by an AFTER trigger (lead_triage_maint
//                     → refresh_lead_triage, writes leads ONLY so #50 attribution is untouched)
//                     + a 15-min pg_cron clock sweep. Same 10-arg signature, same 18 return
//                     columns, PARITY-PROVEN 0/0 both directions across 5 combos vs the old body
//                     (18960/962/11729/2935/15398). Worst-case unfiltered page ~677ms → ~60ms.
//                     Output shape identical, but the deterministic ORDER BY tiebreak
//                     (last_activity_at DESC) can shift LIMIT-boundary rows vs the old planner-whim
//                     order → bump so dev clients drop stale triage pages. Prod auto-evicts via
//                     build-SHA. ([altara]_LOST_LEADS_TRIAGE_PAGE.md; migrations
//                     triage_precompute_step1..8c_*; sql/refresh_lead_triage.sql, sql/trg_lead_triage.sql)
// v184-lead-timeline-dial-dials (Jul 2 2026): LeadTimelineModal dial lane repointed from the
//                     dead dial_events table (shelved DialHQ browser-capture model, test rows
//                     only) to dial_dials (GHL call sync). New row shape: started_at/direction/
//                     status/recording_url/duration_sec — replaces occurred_at/event_type/
//                     disposition/call_purpose. Shows ALL dials now (was: recordings only);
//                     recording player unchanged (dial-recordings bucket signed URLs).
//                     Before the fix leads showed no dial history at all — dial_events had
//                     no real rows. ([altara]_DIALER_TRACKER_ULTIMATE.md G92/G99;
//                     scripts/sweep_dial_calls.py + link_dial_leads.py populate the data)
// v185-dialer-scorecard-hourly-clock (Jul 3 2026): get_dialer_scorecards RPC gained a per-dialer
//                     `hourly` key (sparse [{hour, dials}], org-tz hours from raw dial_dials)
//                     feeding the new radial 24h activity clock on each Dial Analytics dialer
//                     scorecard (DialerActivityClock.tsx — DM SessionClockChart face mirror,
//                     embedded in DialerScorecard.tsx). Cached dial_scorecards payloads predate
//                     the key — without eviction the hook serves cards whose `hourly` is
//                     undefined and the clock renders bar-less until revalidation.
//                     ([altara]_DIALER_TRACKER_ULTIMATE.md P0.dial-analytics FOLLOW-UP;
//                     sql/get_dialer_scorecards.sql)
// v186-dialer-scorecard-role-split (Jul 3 2026): get_dialer_scorecards gained `role`
//                     (organization_members.role via LATERAL, null when no membership row;
//                     inactive members now EXCLUDED per the Jul-3 roster doctrine). The
//                     Dialer Performance grid groups scorecards into Dialers / Closers
//                     sections with a divider (Mako: closers making GHL calls — Adrian —
//                     were mixed into the dialer roster). Cached payloads predate the key
//                     → cards would land in a "No role assigned" bucket until revalidation.
//                     ([altara]_DIALER_TRACKER_ULTIMATE.md; sql/get_dialer_scorecards.sql)
// v187-leaderboard-prestige-total (Jul 3 2026): leaderboard range mode now ranks, sorts,
//                     and displays by the PRESTIGE TOTAL (lb points + KPI ±1 + MVP,
//                     Nathaniel's REF:370-374 winner rule) instead of lb points alone.
//                     useLeaderboard gained get_kpi_delta_sums RPC + active-roster
//                     sidecar fetches and filters inactive reps pre-setState;
//                     prestige_points_readable now exposes kpi_points/mvp_points;
//                     calculate_rankings gained a zero-activity gate + stale-row
//                     zero-out. Before the fix June showed Adrian #1 (116 lb vs Beryl
//                     115) while the true total is Beryl 119 vs Adrian 108, and
//                     inactive reps (Davin) still rendered. Cached daily_leaderboard
//                     rows predate the roster filter → stale boards until eviction.
//                     ([altara]_PRESTIGE_SYSTEM_BUILD.md P0-LBTOTAL S1-S5)

// v188-registration-prefill (Jul 4 2026): validate_registration_link RPC now returns
//                     prefill_data / required_fields / welcome_content on the link{} object.
//                     The registration wizard seeds form fields from prefill_data, drives
//                     per-link required fields off required_fields, and renders an admin
//                     welcome note + Mako's signature on WelcomeStep. Frontend guards the new
//                     fields with `??` so it is inert until migration
//                     2026-07-04_registration_prefill_fields.sql is applied. Bump evicts any
//                     dev caches predating the new validate shape.
//                     ([altara]_CLIENT_REGISTRATION_PORTAL.md /ver PLAN Phase B/C)

// v189-teamcontext-prestige-month-bounded (Jul 4 2026, P2 diff-sweep fix):
//                     TeamContext.tsx prestigePoints fetch changed from
//                     `.order("total_points", desc).limit(200)` to a 3-calendar-month
//                     bounded window (`.gte("month", cutoff).order("month", desc)`).
//                     At 200-300 reps/org with several months of monthly prestige
//                     rollups, the old all-time total_points ordering could push a
//                     rep's CURRENT/covered month row past row 200, so LeaderboardPage's
//                     prestige_total silently read mvp_points=0 for that rep. Harmless
//                     at today's ~10 reps; a real bug at CLAUDE.md's mandated 200-300
//                     rep target. Cached `altara_*_team_prestige` entries from before
//                     this fix may hold the old all-time-ordered top-200 shape — bump
//                     evicts them so clients refetch the month-scoped rows.
// v190-triage-geo (Jul 4 2026): get_closer_triage_list now returns two new columns
//                     (lead_timezone, lead_country) for the Lost Leads local-time +
//                     country-filter features, and accepts a p_countries filter arg.
//                     Cached `altara_*_triage_*` RpcRow[] entries from before this
//                     change lack the two columns → the row would show the org-fallback
//                     time until the 2-min TTL healed it; the bump evicts them so the
//                     first render already carries the lead's own zone/country.
//                     NOTE: activates only once migration
//                     sql/migrations/2026-07-04_triage_geo_timezone_country.sql is
//                     applied to the DB — the frontend is null-safe before then.
// v191-dial-connect-rate (Jul 4 2026): Dial Analytics Connect Rate now means REAL
//                     connects, not raw pickups. daily_dial_metrics gained a
//                     real_connects column (completed/answered AND duration_sec>=30);
//                     get_dial_analytics_fast + get_dialer_scorecards repoint
//                     connect_rate = real_connects/dials (was connected/dials, which
//                     counted 2-second hangups + NULL-duration machine answers ~3x
//                     high — G112), conversation_rate now /real_connects, and the
//                     scorecard's Connect-to-Book RATE was DROPPED (booked shown as a
//                     plain count). Cached dial_analytics + dial_scorecards payloads
//                     from before this carry the old inflated connect_rate (~53%) and
//                     lack the real_connects field → the bump evicts them so the first
//                     render shows the ~18% real rate. (Mako Q14 ruling 2026-07-04;
//                     [altara]_DIALER_TRACKER_ULTIMATE.md; sql/get_dial_analytics_fast.sql
//                     + sql/get_dialer_scorecards.sql + 2026-07-04_dial_real_connects_column)
// v192-dial-connect-floor-15s (Jul 4 2026): connect floor lowered 30s → >15s
//                     (Mako: "more than 15 sec let's do that instead"). A real
//                     connect is now completed/answered AND duration_sec > 15 (was
//                     >= 30) — changed in refresh_daily_dial_metrics (+ backfill of
//                     the stored real_connects), get_dial_analytics_fast (hourly),
//                     get_dial_due_queue (contacted), and get_dialer_connects. The
//                     Dial-Due "contacted" cadence exit follows (a >15s call now
//                     drops a lead from the cadence). Cached dial_analytics /
//                     dial_scorecards / dial_due / dial_connects payloads carry the
//                     old 30s connect counts → bump evicts them so the first render
//                     shows the higher >15s connect rate. Labels "(30s+)" → "(>15s)".

// v193-dial-due-source-connect-recurl (Jul 5 2026): two Dial Analytics payloads
//                     gained emitted fields. get_dial_due_queue now emits `source`
//                     (the arrival event_type — typeform_submission / webinar_signup)
//                     on each `queue` row and `no_contact` lane row (it already
//                     computed it, only never_dialed emitted it) → powers the new
//                     Typeform/Webinar funnel chip in the Due Queue. get_dialer_connects
//                     now returns `recording_url` (the bucket path) alongside the
//                     existing `has_recording` bool → powers the inline RecordingPlayer
//                     on each recorded row of the per-dialer Connects panel. Cached
//                     dial_due / dial_connects payloads predating this carry no
//                     `source` / `recording_url` → the chip renders blank and the
//                     inline player is absent until eviction. RPC bodies applied live.

// v194-dial-funnel-everywhere (Jul 5 2026): the funnel chip + inline playback +
//                     lead-timeline click-through now cover EVERY Dial Analytics
//                     lead surface, not just the two added in v193 (Mako: "this
//                     isn't enough... a lot you haven't done for the UI"). Two RPC
//                     payloads gained fields: get_dialer_connects now emits `source`
//                     (arrival funnel — typeform/webinar/call_booked) on each connect
//                     → funnel chip on the per-dialer Connects panel. get_dial_due_queue
//                     now emits `recording_url` + `source` on each `all_dials` row →
//                     inline RecordingPlayer + funnel chip on the raw All-Dials feed.
//                     Funnel vocabulary unified via utils/funnelLabel.ts, which ADDS
//                     the previously-missing "Call Booked" label (call_booked was a
//                     real 3rd funnel — 5.8k Rayston arrivals, 312 dials/7d — that
//                     rendered no chip before). Cached dial_connects / dial_due
//                     payloads predating this carry no `source` / `recording_url` →
//                     chips render blank, All-Dials players absent until eviction.
//                     RPC bodies applied live.

// v195-june-prestige-crown (Jul 6 2026): June prestige crown committed for
//                     Rayston — process_monthly_prestige promoted Alice (setter)
//                     and Samantha (dialer) from Prestige I → II (prestige_ranks
//                     current_level 1→2). No closer winner — Beryl, the rank-1
//                     closer, was excluded (rank_in_pod nulled) and marked
//                     organization_members.status='inactive' after exiting the
//                     company; Adrian buffered at level 1. get_rep_portal_data +
//                     leaderboard RPCs read prestige_level from prestige_ranks.
//                     current_level; cached rep-portal / board payloads predating
//                     the crown still carry level 1 → Alice's + Samantha's
//                     PrestigeBadge renders the red P1 emblem instead of the green
//                     P2 until eviction. Data-only change, no payload shape change.
// v196-triage-flat7-hold (Jul 7 2026): claim hold-expiry model changed from a
//                     3-day base + 7-on-booking to a FLAT 7-day hold, via the new
//                     SSOT function triage_claim_hold_expires (collapses the formula
//                     that was hand-copied into 5 sites across 4 fns). get_closer_
//                     triage_list / _facets / _country_facets read
//                     leads.triage_hold_expires_at (recomputed by the migration
//                     backfill); get_lead_claim_history now returns longer days_left.
//                     Pool composition unchanged at apply time (only held-lead
//                     countdowns lengthen), but cached pool + Claims-tab payloads
//                     predating the apply carry the old shorter hold → evict so
//                     countdowns match the live DB. Migration
//                     2026-07-06_triage_flat7_hold_ssot.sql applied to the shared DB
//                     2026-07-07. Reversible: swap the SSOT function body (see
//                     [altara]_LOST_LEADS_TRIAGE_PAGE.md "Flat-7 Hold — Revert Method").
// v197-sop-view-as-role (Jul 13 2026): SOP Library read RPCs widened to accept an
//                     optional p_view_as_role — get_org_agent_docs_index(uuid,text)
//                     and get_org_agent_doc_detail(uuid,uuid,text). When an exec
//                     "views as" a team member, the frontend now passes that member's
//                     role so the server simulates their visibility (drops the
//                     GOD/OVERSIGHT bypass to false, applies the real 4-tier filter);
//                     without it a super-admin/exec saw every SOP regardless of who
//                     they were viewing as (the "clients can see exec-only SOPs"
//                     artifact — display only, never a real leak). Payload shape is
//                     unchanged; the cached index/detail responses predating this can
//                     hold the GOD-view doc set for a view-as session, so evict.
//                     Migration sql/org_agent/012_view_as_role.sql applied to the
//                     shared DB 2026-07-13 (arity widen resolves the old 1-arg call
//                     via the default param — no prod caller break). This single
//                     eviction ALSO covers 013_sop_viewer_grants.sql (same day):
//                     it re-replaced the read RPCs to add the per-user grant
//                     OR-branch (same response shape — SOP hooks fetch fresh anyway,
//                     so this bump is the belt-and-suspenders global evict).
// v199-per-user-display-currency (Jul 16 2026): currency system Phase 2 — the
//                     display-currency toggle went from a hardcoded exec USD⇄AUD
//                     (2 emails) to PER-USER, driven by profiles.display_currency
//                     (new column; Rayston client → SGD, Nathaniel + Mako → AUD).
//                     No RPC/response shape changed — money is still USD in the DB
//                     and converted at DISPLAY only. Bump is the belt-and-suspenders
//                     evict so any page cached before the per-user resolver landed
//                     re-renders under the resolved currency. localStorage key
//                     "altara_currency_mode" stays safe-listed (survives the bump).
//                     Migration sql/migrations/2026-07-16_profiles_display_currency.sql.
// v200 (Jul 22 2026) — DM Health "New DMs" vs "Conversations Created" split.
//                     get_dm_analytics_fast daily_breakdown gained new_dms_lead_replied
//                     (live per-day lead-replied count). Daily Activity + Engagement Depth
//                     charts now read it as the honest "New DMs" series; the raw all-source
//                     count is charted separately as "Conversations Created". Also relabeled
//                     the setter-touched metric "Engaged" -> "Replied" everywhere on the tab
//                     (metric unchanged; the two-way 2+2 metric keeps "Engaged" on the funnel).
//                     RPC response shape changed (new key) -> evict stale altara_dm_* caches.
// v201 (Jul 31 2026) — get_dm_analytics_fast date window unified to org-tz on the portal.
//                     usePortalKpis.ts passed bare "YYYY-MM-DD" to a p_start_date/p_end_date
//                     that is timestamptz (body does `(p_start_date AT TIME ZONE tz)::DATE`),
//                     while useDMAnalytics.ts passed a full ISO instant. For any org at a
//                     negative UTC offset the two resolved to windows ONE DAY apart on BOTH
//                     bounds -- live-probed America/Chicago: 06-30..07-30 vs 07-01..07-31 --
//                     so two blocks on the same client-facing page were computed off different
//                     days. Both call sites now use toOrgStartISO/toOrgEndISO (commit 9af22b60).
//                     Response SHAPE is unchanged; the requested WINDOW changed, and both hooks
//                     key their localStorage entries on calendar day only, so a pre-fix entry
//                     would keep serving wrong-window numbers under a matching key for up to its
//                     TTL (10 min usePortalKpis, 24 h useDMAnalytics historical). Bumping to
//                     evict those. Asia/Singapore (Rayston, the only org with DM data) resolves
//                     identically before and after -- verified against live Postgres on both
//                     bounds -- so no rendered Rayston number moves.
// v202 (Aug 10 2026) — Evict calendar event lists holding deleted e2e fixture rows.
//                     tests/calendar-pendvis-filter-vocab.spec.ts SETUP writes a lead +
//                     call_booked + pending_close SCO per run, and cleanup is MANDATORY
//                     AND EXTERNAL (transactions has no DELETE policy for `authenticated`,
//                     so the spec cannot remove its own rows). Five runs during the S87
//                     flyout build left 5 leads / 10 transactions live in Rayston, each
//                     carrying expected_amount 7500 -- the calendar sidebar's entire
//                     "Pending Closes 5 / $37,500" for Aug 7-10 was fixture data against a
//                     true 0 rows / $0. Rows are now deleted (child-first per the FK 23503
//                     ordering) and verified gone from transactions, leads AND
//                     transactions_readable. This bump exists because the ROWS changed, not
//                     the shape: `getEffectiveTTL` promotes a fully-past range's events
//                     cache to the 24 h historical tier, so a tab that loaded Aug 9 while
//                     the fixtures were live keeps rendering the deleted chips for a day.
//                     Enrichment sits on the 1 h tier and had already recomputed, which is
//                     why the chips flipped from "Pending" to "+ Outcome" instead of
//                     disappearing -- a stale events list under a fresh enrichment pass.
//                     No RPC, formula or payload shape changed; nothing recomputes differently.
// v203 (Aug 10 2026) — Prestige III emblem art landed. public/prestige/emblem-p3.png is new
//                     (508x512, transparent, from Mako's medallion_transparent_cropped.png),
//                     and prestigeConfig[3] in PrestigeBadge.tsx now carries `image` so level 3
//                     renders the medallion instead of the grey gradient-circle placeholder.
//                     Commissioned because Alice reaches Prestige III on the July crown -- the
//                     handoff's "P3-P12 art is deprioritized, not blocking until someone
//                     reaches Prestige III" park fired its own condition. prestigeConfig[3]
//                     gradient also moved amber/orange -> slate/blue to match the new art, and
//                     that gradient is what the highest-achieved corner pip renders, so a rep
//                     whose HIGHEST is 3 gets a recoloured pip even at a lower current level.
//                     No RPC, formula or payload shape changed. This bump is purely to evict
//                     cached leaderboard/rep-portal payloads whose badge markup was rendered
//                     against the placeholder branch (`hasEmblem` was false for level 3, and
//                     that also gated the 5-star row via `showStars && !hasEmblem`).
// v204 (Aug 12 2026) — Close rate's denominator changed from `shows` to `decisive_calls`
//                     (shows - reschedules) on BOTH rollup tables, and both gained a second
//                     rate column. Applied live by
//                     sql/migrations/2026-08-12_closerate_decisive_denominator.sql.
//                     daily_org_metrics.close_rate is now high_ticket_deals / decisive_calls
//                     and daily_leaderboard.close_rate is wins / decisive_calls; the new
//                     pending_close_rate on each adds pending_closes to the numerator.
//                     Numerators are deliberately different per table -- fixing one and not
//                     the other IS the drift this migration exists to kill. Ruled by Mako
//                     2026-08-09 (R21 reschedules must not count against close rate, R24
//                     `decisive_calls` is canonical, R29 two rates everywhere, R30 the
//                     leaderboard scores on the final rate and only displays pending).
//                     get_daily_org_metrics went 23 -> 24 returned columns, so its cached
//                     payload SHAPE changed, not just its values. Cached responses hold the
//                     old (wins+pending)/shows number under the SAME column name
//                     `close_rate` -- the column keeps its name and changes meaning
//                     underneath, which is exactly the "new number under an old caption"
//                     shape, so an unevicted cache shows a stale rate with no visible tell.
//                     Precedent: v173 bumped for these same two generated columns on Jun 19.
//                     DB was applied FIRST, then this bump, so evicted clients refetch
//                     already-migrated data rather than re-caching the pre-migration shape.
// v205 (Aug 13 2026) — Close-rate propagation to the 8 remaining live functions that each
//                     carried their own copy of the formula. v204 fixed only the two
//                     generated columns; these functions never read them, they recompute.
//                     Applied live by
//                     sql/migrations/2026-08-13_closerate_decisive_propagation.sql
//                     (plan entries S21-S27 + S30). Every one of them now returns
//                     wins / decisive_calls where decisive_calls = shows - reschedules,
//                     which is byte-for-byte what daily_leaderboard.close_rate generates.
//                     Affected RPCs and routes: get_client_portal_metrics (/admin/portal),
//                     get_portal_rep_cards (/admin/portal rep cards), get_rep_portal_data
//                     (/admin/rep-portal), both get_exec_dashboard_data overloads
//                     (/admin/mastermind), _compute_weekly_stoplight_row (stoplights),
//                     get_funnel_metrics (/admin/dashboard funnel) and
//                     get_pipeline_kpis_aggregate (pipeline KPIs).
//                     NO return shape changed -- every edit is an expression inside a body,
//                     so this is a pure VALUE bump, the harder kind to spot. `close_rate`
//                     keeps its name and changes meaning underneath, so an unevicted cache
//                     shows the old number with no visible tell. Measured on Rayston
//                     7/14-8/12: /admin/rep-portal 49.7 -> 54.9, pipeline KPIs 49.7 -> 54.9,
//                     /admin/portal 50.9 -> 55.3, rep cards 50.6 -> 55.8, mastermind
//                     50.6 -> 55.1 and 48.2 -> 55.1, stoplights 49.39 -> 56.64, funnel dm
//                     45.0 -> 49.1. Every route moves UP, because R21 only shrinks the
//                     denominator and dropping pending_close only shrinks the numerator.
//                     NOT in this bump: get_funnel_cohort_metrics + get_webinar_events
//                     (S28/S29), aborted on measurement -- their `shown` is a deny-list
//                     (status NOT LIKE '%no_show%') carrying no reschedule count.
//                     DB applied FIRST at 00:36 Manila, then this bump.
// v206 (Aug 13 2026) — Close-rate LAYER 5. The 5 remaining TypeScript compute sites.
//                     v204 did the generated columns, v205 did 8 SQL functions, and
//                     both left the frontend alone. S16 shipped 4 layer-5 sites on
//                     2026-08-12 and was recorded as "layer 5 done". It was layer 5
//                     PARTIALLY done. These 5 sites were never in its scope, and never
//                     in the S21-S30 inventory either, because that inventory was built
//                     from live pg_proc and is SQL-only by construction. A hook computing
//                     a metric in TypeScript leaves no trace in any SQL catalog.
//                     Sites moved to wins / decisiveCalls (decisiveCalls = shows −
//                     rescheduled, R21/R24/R30):
//                       hooks/usePortalKpis.ts:163        -> /admin/portal
//                       hooks/usePreCallCompliance.ts:524 -> /admin/precall-compliance
//                       hooks/usePreCallCompliance.ts:588 -> same page, setter rows
//                       utils/revivedMetrics.ts:249       -> /admin/dashboard RevivedLeadsPanel
//                       utils/selfSetMetrics.ts:147       -> /admin/dashboard SelfSetPanel
//                     WHY THE BUMP IS MANDATORY HERE: usePortalKpis and
//                     usePreCallCompliance both cache the DERIVED object, not the raw RPC
//                     response, so the old closeRate VALUE is what sits in localStorage.
//                     This is not the "derived from fields already in the payload" case
//                     that skips a bump — the cached number itself is stale.
//                     /admin/portal was SPLIT before this: it renders its Close Rate tile
//                     from usePortalKpis, while v205 had moved get_client_portal_metrics
//                     underneath it. Two sources, one fixed, ~5 points apart. Same shape
//                     as B16. usePortalKpis also carried a comment claiming it was copied
//                     verbatim from useOperationsAnalytics and must stay identical; S16
//                     had fixed one and not the other, which is how the split happened.
//                     usePortalKpis gains pendingCloseRate + decisiveCalls to match its
//                     twin (R29 groundwork; nothing renders them yet, that is 86d40q2nn).
//                     revivedMetrics + selfSetMetrics also gain decisiveCalls; both are
//                     still named in B4 for the SEPARATE show-rate orphan bug, untouched.
// v207 (Aug 13 2026) — Close rate FINISHED. Mako's ruling: "everything should be canonical".
//                     Three things landed together:
//                     (1) S28 get_funnel_cohort_metrics + S29 get_webinar_events, the two
//                     that aborted on 2026-08-13 00:36, are now canonical. They divided by
//                     a DENY-list (`status NOT LIKE '%no_show%'`) measuring 191 rows where
//                     canonical decisive_calls is 147 — it admitted pending_superseded 10,
//                     paying_installment 7 ($4,291 of recurring cash) and unique 6. Applied
//                     by sql/migrations/2026-08-13_s28_s29_canonical_decisive.sql. All 10
//                     SQL functions are now on wins / decisive_calls.
//                     (2) All 9 REPO MIRROR .sql files brought onto the same formula. They
//                     were the replay source (`\i sql/analytics/<fn>.sql`) and had drifted
//                     BEHIND live, so a redeploy would have silently reverted f790c412 —
//                     the dont-touch.md:19 trap. Proven by replaying every mirror inside a
//                     ROLLBACK and diffing against live: 8 of 8 sites reproduce exactly.
//                     (3) THE ONLY REASON THIS BUMP IS REQUIRED, and it is not (1) or (2):
//                     /admin/precall-compliance captions were wrong. They read
//                     "Close Rate (74 wins / 132 outcomes)" over a real divisor of 134, and
//                     a legend saying "Wins ÷ (Wins + Losses)". Both understated it —
//                     decisive_calls also carries pending_close. Fixed by plumbing
//                     withPreCallDecisive / withoutPreCallDecisive through
//                     usePreCallCompliance, which CACHES ITS DERIVED OBJECT. A cached entry
//                     predating this has no such field, so the caption would render
//                     undefined. That is a payload SHAPE change, hence the bump.
//                     NOT a reason to bump, stated so nobody re-derives it: S28 and S29
//                     render nowhere. get_funnel_cohort_metrics has no hook at all, and
//                     get_webinar_events reaches only components/funnel/SankeyFunnel.backup.tsx,
//                     which nothing imports. useWebinarEvents has zero localStorage writes.
//
// v208 (2026-08-13) — showRate S39. get_funnel_metrics gained the canonical R14 orphan
//                     term on BOTH its by_source and dm show rates. This is a live VALUE
//                     change on a rendered page: /admin/dashboard reads it through
//                     useFunnelHealth.ts. Measured Rayston 2026-07-14..08-12 —
//                     dm 98.4 → 78.9, webinar 97.1 → 70.2, typeform 73.7 → 53.8.
//                     A cached RPC response predating this holds the OLD rate, so the
//                     dashboard would keep showing ~98% until eviction. Hence the bump.
//                     NOT a reason to bump, stated so nobody re-derives it: S34's
//                     get_portal_rep_cards moved 93.3 → 66.0 in the database and has NO
//                     live TS caller at all (grep 2026-08-13 — only tests/ and the
//                     comments in this file), so nothing caches it. S36 changed comments
//                     only in revivedMetrics.ts / selfSetMetrics.ts, no derived object moved.
//
// v209 (2026-08-13) — Dial AP-1: the 45s connect floor AND attempt collapsing, applied
//                     live together in one transaction. Both a VALUE change and a SHAPE
//                     change on a rendered page, so this bump is mandatory twice over.
//                     VALUES, Rayston July 2026: real_connects 3,555 → 1,216 (the floor
//                     moved >15s → >=45s per Mako 2026-08-09 FINAL), and total_dials
//                     16,856 → 15,556 because two dials to the same lead by the same
//                     dialer inside 5 minutes are now ONE attempt unless the earlier
//                     cleared 45s. Connect rate reads 7.82% collapsed / 7.21% raw, down
//                     from 21.1% at the old floor.
//                     SHAPE: get_dial_analytics_fast and get_dialer_scorecards each gained
//                     a 4th arg (p_count_mode) and new response keys — raw_dials, attempts,
//                     count_mode, and summary.spared_count. The old 3-arg arity was DROPPED
//                     in the same transaction, deliberately: leaving it would have created a
//                     second overload, and the frontend's 3-named-param call would then match
//                     both via the default, raising 42725 "function is not unique" and
//                     blanking every dial card. Dev and prod share this database, so that
//                     would have hit both at once.
//                     DB was applied FIRST, then this bump, so evicted clients refetch
//                     already-migrated data rather than re-caching the pre-migration shape.
//                     ⚠ This bump does NOT reach prod until a push to main. Prod reads the
//                     same database and is already serving the new numbers, so until that
//                     push prod shows new values under its own older bundle. Accepted
//                     explicitly by Mako 2026-08-13 ("yes accept go").
//                     ALSO FOLDED IN (same version, neither has shipped): get_dial_due_queue's
//                     CONTACTED cadence exit moved > 15s -> >= 45s, so a voicemail no longer
//                     drops a lead out of the dial cadence. Applied live the same session.
//                     Its own surface, the Dial Due tab, is PARKED and hidden as of the same
//                     day (Mako: "hide that tab for now and park it"), so nothing renders that
//                     payload today and the bump is belt-and-braces rather than load-bearing
//                     for it.
// v210 (2026-08-14) — P-CLAIM-COOLDOWN: the Lost Leads pool now hides leads any rep
//                     claimed within the last N days, default 30, rep-switchable to
//                     off/30/60/90/120 from the Filters panel. SHAPE: all three triage
//                     RPCs gained p_claimed_within_days — get_closer_triage_list 11 args
//                     → 12, get_closer_triage_facets and get_closer_triage_country_facets
//                     2 → 3. Each old arity was DROPPED in the same transaction as its
//                     re-creation, deliberately: a wider overload beside the old one makes
//                     PostgREST ambiguous, and dev and prod share this database, so it
//                     would have blanked the pool on both at once.
//                     VALUES, Rayston live at apply time: pool 17,520 with the cooldown
//                     off, 15,791 at 30 days, 15,739 at 60/90/120. Those last three are
//                     identical only because the oldest claim on this org is 34 days old,
//                     so any window ≥60 already captures every claim that exists. They
//                     diverge as claim history accrues. Not a bug.
//                     WHY the cooldown is NOT in hasActiveFilters: it redefines the POOL,
//                     like minDaysDead, rather than filtering within it. Counting it as a
//                     filter would freeze poolTotal, which only updates on an unfiltered
//                     fetch, and the Total tile would then contradict the visible list.
//                     For the same reason the cooldown is passed to BOTH facet RPCs, so the
//                     heat band and country chips count the same population as the list.
//                     getCacheKey also gained a _cd{N} suffix — two cooldown settings are
//                     two different pools and must never share a cache entry.
//                     DB was applied FIRST (2026-08-14), then this bump, so evicted clients
//                     refetch already-migrated data instead of re-caching the old shape.
//                     ⚠ Does NOT reach prod until a push to main. Prod reads the same
//                     database; the migration is backward-compatible (the new arg defaults
//                     NULL), so the deployed bundle keeps working and simply shows no
//                     cooldown until that push.
// v211 (2026-08-14) — E2E-PROD-CLEANUP: not a shape change, a DATA correction. The
//                     Playwright calendar/show-confirmation suite had written 54 fixture
//                     transactions + 29 leads straight into the prod Rayston/Dynamis orgs
//                     over 2025-07 → 2026-08, including 4 fake paid_in_full rows worth
//                     $20,000 cash / $12,000 revenue in Rayston's live numbers. They never
//                     self-cleaned: transactions/leads have NO `FOR DELETE` RLS policy, so
//                     the tests' authenticated client physically cannot delete what it made.
//                     Removed out-of-band via service role (scripts/e2e-cleanup/cleanup-e2e.mjs),
//                     signature = RFC-reserved email domains only (.invalid/.test/@example.*).
//                     RPC response SHAPE is byte-identical; bumping only so LOCAL-DEV cached
//                     aggregate dashboards (Operations, Rep Portal) that summed the fake week
//                     re-fetch. Prod auto-evicts on the next push via __BUILD_COMMIT__.
// v212 (2026-08-14) — O3 of plan-orphan-ondemand. useLeaderboard now calls
//                     ensure_orphans_fresh(org, from, to) before reading
//                     daily_leaderboard_readable, so orphan_precalls (and the STORED
//                     GENERATED show_rate derived from it) recompute on demand instead
//                     of waiting for the nightly cron. Cached leaderboard payloads hold
//                     the PRE-death-dating orphan counts and the show rates computed
//                     from them, so they must be evicted: R50 moved which DAY an orphan
//                     lands on, and O2's anti-join sweep cleared 11 orphans that were
//                     double-counted at their old slot dates. Response SHAPE is
//                     unchanged — the values inside it moved.
//                     ⚠ A SECOND bump is required after O5 (the daily_org_metrics leg);
//                     this one does not cover that formula flip.
//
// v213 (2026-08-14) — O6 of plan-orphan-ondemand, the planned THIRD bump (O5's
//                     is still owed). Four repoints applied live the same hour:
//                     get_rep_portal_data + get_portal_rep_cards orphans now read
//                     daily_leaderboard (death-dated, R50); get_pipeline_kpis_aggregate
//                     orphan term ignores p_sectors (all-reps 131 -> 72, show_rate
//                     67.7 -> 77.3); get_operations_kpis rule (d) went precall-bound
//                     per O-R2 (orphans 77 -> 63) AND the dormant Jun-23 distinct-lead
//                     orphan_rate denominator rode the mirror live (26.7 -> 29.9 at
//                     SQL grain; strip renders 25.0 -> 27.7). Cached RPC responses on
//                     /admin/rep-portal, /admin/setter-timelines and /admin/dashboard
//                     hold pre-repoint orphan values — evict. SHAPE unchanged
//                     everywhere except one ADDITIVE key (pre_calls_distinct_leads).
//
// v214 (2026-08-15) — O5 of plan-orphan-ondemand: the SECOND orphan bump v212
//                     promised. daily_org_metrics gains orphan_precalls and its
//                     generated show_rate flips shows/calls_booked -> canonical
//                     R14 shows/(shows+no_shows+orphan_precalls); the backfill
//                     recomputed ALL history (August aggregate 79.3 -> 55.2,
//                     death-dated). ensure_orphans_fresh is v2 (org-grain leg).
//                     Every cached response reading org show_rate holds the old
//                     formula's values — evict. SHAPE: one ADDITIVE column.
// v215 (2026-08-15) — R52 = receipt #9 mechanism ruling, option (a): the 5 inline
//                     lead-grain orphan copies death-date per R50 (was: pre-call's
//                     own slot). get_operations_kpis Aug orphans 42 -> 39 (show-up
//                     57.0 -> 59.0 on /admin/dashboard); funnel typeform 46.2 -> 50,
//                     webinar 50 -> 55.6; cohort direct 53.8 -> 55.3; webinar events
//                     66.7 -> 72.7 / 61.5 -> 68.1 (frozen Jul-18 event held 75.0
//                     exactly — R50's frozen-history promise). Cached RPC responses
//                     on /admin/dashboard + funnel/webinar surfaces hold slot-dated
//                     orphan values — evict. SHAPE unchanged, values only.
// v216 (2026-08-15) — B10 + P0-D: the last two old-formula show-rate surfaces go
//                     canonical R14. get_exec_dashboard_data show_rate +
//                     prev_show_rate move shows/calls_booked -> shows/(shows+
//                     no_shows+orphan_precalls) and APPEND 3 columns
//                     (prev_no_shows, orphan_precalls, prev_orphan_precalls) —
//                     /admin/mastermind Rayston 80.3 -> 66.3 (commit 64c9467b).
//                     LeaderboardPodium + SetterSnippet + PerformanceSnippet gain
//                     the orphan term client-side on /admin/dashboard +
//                     /admin/portal (commit ebd0527d; podium overstated 12-28pp,
//                     always flattering). SHAPE: 3 ADDITIVE exec columns; cached
//                     exec/leaderboard responses hold inflated rates — evict.
// v217 (2026-08-15): R9 qualified grain — get_operations_kpis calls_booked +
//                     calls_booked_settled moved to COUNT(DISTINCT lead_id)
//                     (158→121 / 302→219 on the pinned windows); usePortalKpis
//                     cashPerBooked adopted the settled denominator (B7 twins
//                     re-synced). VALUE change, same shape — cached KPI payloads
//                     hold row-grain booked counts — evict.
// v218 (2026-08-17): R53-R58 wave — daily_org_metrics.calls_booked repointed to
//                     qualified distinct-lead grain (writer + full-history recompute;
//                     exec Mastermind/OrgDetailDrawer sums move, winB 205→223);
//                     get_client_portal_metrics.show_rate canonical R14 (73.4→67.1
//                     winB, flakey/ghost split retired); new get_show_rate_breakdown
//                     RPC + Show-up modal. VALUE changes on cached exec + portal
//                     payloads — evict.
// v219 (2026-08-17): newDMs/conversations wave (R4-R7 + R47) — exec total_new_convos
//                     repointed raw→canonical lead-replied (10,669→3,804 on the 30d
//                     window, 2.8x drop on /admin/mastermind); stoplight new_dms key
//                     canonical (2,639→1,011 wk Aug 10); get_dm_analytics_fast
//                     total_real_convos + daily real_convos now read real_convos_v2
//                     (+43 W30); conversion_rate generated col regenerated on v2; TS
//                     keys newDMs→newDMChats + conversionRate→conversationRate, hook
//                     dmStats field renamed — cached derived objects hold the OLD
//                     field name AND old values — evict.
// v220 (2026-08-18): DM reply-turn lead-last fix — get_reply_turn_distribution's
//                     reply_turn_*_lead counts now include ManyChat-flipped rows
//                     (last_message_from='setter' AND last_lead_msg_at >
//                     last_setter_msg_at), not only last_message_from='lead'. Deep
//                     buckets read ~20-24% higher, bucket 0 unchanged; ~1,073 convos
//                     org-wide were undercounted (commit df5eef83). The reply-turn
//                     bar values are cached by useReplyTurnDistributionAll +
//                     useDMAnalyticsPrefetch — evict so the scorecard / DM-Health
//                     bars refetch the corrected counts. (The new Unreplied metric
//                     and the get_pattern_sample_messages name fix use no-persistence
//                     hooks, so they need no eviction.)
// v221 (2026-08-18): B9 — client portal "DM Conversations" chart series repoints
//                     from new_dms + real_convos (double-count; subset summed onto
//                     its superset) to real_convos_v2 per R19 + R7. 30d sum
//                     12,007 -> 1,570. usePortalCharts caches the derived
//                     PortalChartData object in localStorage — evict so the
//                     sparkline refetches the conversations-only series. Same
//                     wave: 3-arg exec zombie 196315 DROPPED (no cached shape,
//                     but the parity tombstone + bare-string resolution change
//                     ride this version marker for the receipt).
// v222 (2026-08-18): DM lane close — engagementRate DELETED (R61, redundant with
//                     conversationRate) + outboundNewDMs DELETED (R62, zero signal:
//                     GHL 1:1 with ManyChat sends). useDMAnalytics no longer emits
//                     engagementRate; the DM Health "Replied" card drops its rate
//                     sub-line + color. Evict so cached DMAnalyticsData objects
//                     carrying the stale field refetch the trimmed shape.
// v223 (2026-08-19): Dial click-to-seek — get_dial_proof_rows now returns an 18th
//                     field, transcript_json (per-word timestamps), grafted on via a
//                     LEFT JOIN dial_dials in the wrapper. DialProofRow gained
//                     transcriptJson. Cached get_dial_proof_rows payloads from the
//                     17-field shape lack the key, so the proof modal would keep
//                     rendering the flat transcript (no clickable words) until evicted.
// v224 (2026-08-21): P0-B — get_forecast_data now dates SCO cash/revenue by
//                     call_start_time (canonical, debug #13), not event_date. The
//                     forecast tiles on /admin/dashboard move (Aug cash -$21.5k,
//                     revenue -$36k on Rayston). Evict cached get_forecast_data
//                     payloads still carrying the event_date-basis sums.
// v225 (2026-08-24): P0-H — get_winners_analytics + get_winner_conversations date
//                     SCO cash by COALESCE(call_start_time, event_date), not bare
//                     event_date (recon Lane A, debug #13 class). /admin/winners cash
//                     moves (Rayston: Jul +$12.5k -> 373,176, Aug -$20k -> 207,557).
//                     Evict cached winners payloads on the old event_date basis.
// v226 (2026-08-25): phone-setter (dialer) commission — RepSale gains bucket +
//                     split_with, dialers now carry windowed/bucketed sales instead
//                     of the salaried empty statement. Evict cached rep-invoice blobs.
// v227 (2026-08-26): setter-perf tiles live — get_setter_cross_day_metrics gains 8
//                     now()-anchored columns (leak_engaged_7d/leak_untouched_7d,
//                     depth_engaged_30d/depth_deep_30d, cliff_all_24_48h/cliff_deep_24_48h,
//                     actionable_all_lt24h/actionable_deep_lt24h) powering the 4 scorecard
//                     tiles (Live Leak Rate, Depth-Conversion, Deep Cliff-Fall,
//                     Actionable-Deep Owed). The RPC was deployed live to shared prod this
//                     session (strict superset — 15 existing columns byte-identical, parity
//                     proven). Cached cross-day payloads from the 15-column shape lack the
//                     tile fields, so the tiles render 0/— until evicted.
// v228 (2026-08-28): new get_dismissed_leaderboard RPC + org-scoped dismissed
//                     fetch back the Dismissed Leads leaderboard/grid. New RPC
//                     shape + new cache keys, so evict stale altara_* caches.
// v229 (2026-08-31): Webinar +2h Lost-Leads fuse — webinar-no-book leads enter the
//                     triage pool at webinar_start+2h instead of the 7-day floor.
//                     Response SHAPE unchanged; POOL POPULATION grows (~292 net-new
//                     on Rayston), so evict stale altara_triage_* reads on next boot.
// v230 (2026-09-02): Client invoice now carries invoiceData.refunds { count, total }
//                     (Whop refunds for the period, by event_date). total_cash was
//                     already net; this adds the visible reconciling line. New payload
//                     SHAPE on the cached inv_ entry, so evict stale altara_*_inv_ reads.
// v234 (2026-09-12): Live registrant-signups section on the Webinar Heatmap page
//                     (/admin/heatmap). New RPCs get_webinar_signup_dates +
//                     get_webinar_signup_distribution(date) feed a new
//                     useWebinarSignupDistribution hook, which caches the per-date
//                     breakdown under altara_*_webinar_signup_dist_. New persisted
//                     payload shape; no prior consumer (additive), bump evicts cleanly.
// v235 (2026-09-13): Dial-coverage panel added to the same Registrant-signups
//                     section. New RPC get_webinar_dial_coverage(date) feeds a new
//                     useWebinarDialCoverage hook, cached per-date under
//                     altara_*_webinar_dial_cov_. Answers "how many registered
//                     numbers were dialed since signup + who dials most". Additive,
//                     new cache prefix; bump evicts the prior version's keys cleanly.
// v236 (2026-09-14): August prestige crown committed on Rayston. Alyssa (dialer)
//                     P1->P2 green, Samantha (dialer) P2->P1 de-rank; Adrian/Norman
//                     nulled and skipped (Mako ruling: crown only Alyssa). No RPC or
//                     payload-shape change — process_monthly_prestige mutated
//                     prestige_ranks.current_level in the DB, which the cached
//                     leaderboard reads do not re-derive. Bump evicts those reads so
//                     every client refetches the new prestige levels + emblems.
// v237 (2026-09-14): Per-webinar cash panel added to the Registrant-signups
//                     section. New RPC get_webinar_cash_attribution(date) feeds
//                     useWebinarCashAttribution, cached per-date under
//                     altara_*_webinar_cash_attr_. Cash credited ONLY to leads who
//                     attended THAT webinar, attributed to the most recent webinar
//                     before their close (Mako's per-webinar ROI rule). Additive,
//                     new cache prefix; bump evicts prior keys cleanly.
// v239 (2026-09-15): R63 AOV $2,000 floor propagated across the high-ticket
//                     family. get_funnel_metrics (by_source + dm aov),
//                     get_rep_portal_data (period_aov), get_funnel_cohort_metrics
//                     (dormant) now compute aov from separate ht_cash/ht_deals
//                     (a payment plan is always high-ticket; only paid_in_full
//                     under $2,000 cash is a low-ticket downsell). Stoplight +
//                     useMetricSamples aov drilldown inherit the gate. close_rate
//                     / cash / deals_closed byte-identical everywhere. Rep-portal
//                     AOV now equals dashboard AOV. HELD: pipeline + exec aov
//                     (blended, parked) and the low-ticket downsell lane.
// v241 (2026-09-17): Paid-vs-organic added to the Webinar Attendance panel on
//                     /admin/heatmap. get_webinar_attendance now joins
//                     webhook_inbox and returns a per-person `ad` boolean (paid
//                     ad vs organic, same is_ad rule as
//                     get_webinar_signup_distribution). useWebinarAttendance
//                     caches per-date under altara_*_webinar_attendance_, so the
//                     payload SHAPE changed and a live dev cache holds the old
//                     ad-less rows. Bump evicts them. WebinarAttendanceAnalytics
//                     re-slices client-side: show-rate + avg-watch cards per
//                     source and a normalised retention-rate curve (Sep 12 day-1:
//                     paid 23.4% vs organic 43.0% show, 70.9m vs 74.7m watch).
// v242 (2026-09-17): Invoice dialer commission now applies the /resched cycle gate.
//                    classifyDialerBucket (hooks/useInvoice.ts) only tags a close
//                    "overlap" (2.5% split) when a role='setter' DM-setter pre-call
//                    sits in the SAME LIVE cycle — after the most recent cycle-death
//                    event (a LOST outcome, a self_set_claim, or a plain cancel with
//                    no rebook within 1h) that precedes the close. A pre-call from a
//                    dead cycle no longer forces an overlap. fetchDialerCtx now records
//                    dm-setter pre-call dates + cycle-death dates, so the cached
//                    altara_*_inv_ rep blob's bucket/commission changed. Rayston
//                    Aug16-Sep15: Matz Law moved overlap→create, Samantha $1,981.98 →
//                    $2,044.48; Alyssa unchanged.
// v243 (2026-09-18): Webinar metric RPCs repointed from calendar NAME to
//                    calendly_event_type_id (UUID). get_funnel_metrics,
//                    get_webinar_events and get_funnel_cohort_metrics now match
//                    the webinar funnel by UUID read from
//                    organizations.settings->'calendars'->'roles' (discovery
//                    6d150f24, implementation/upsell 35eb03bb) instead of
//                    organizations.webinar_discovery_calendar / _upsell_calendar.
//                    The Aug-8 calendar rename ("- Webinar Applications" →
//                    "- Webinar") had silently zeroed webinar_discovery_booked
//                    (0 while 115 real Sept bookings existed). Same VALUES, same
//                    SHAPE — but the cached webinar/operations RPC blobs hold the
//                    stale zeros, so a bump evicts them. Backed by a full Rayston
//                    calendly_event_type_id backfill (7,530 booked + 4,599 cancels).
//                    Live disc_booked now 42 (Sept) / 84 (Aug).
// v244 (2026-09-19): Calendar-grid + Calendly-heatmap grouping repointed from
//                    calendar NAME to calendly_event_type_id (UUID). The grid
//                    (/admin/calendar getCallType/getEffectiveCallType) and the
//                    heatmap (/admin/calendly transactionsToCalendly eventTypeName)
//                    now resolve the display name via the shared CALENDAR_UUID_TO_NAME
//                    map keyed on the UUID, falling back to status for rows with no
//                    UUID. Both fetches (useCalendarData.ts, CalendlyHeatmapPage.tsx)
//                    added the calendly_event_type_id column to their .select(), so
//                    the cached payload SHAPE changed; and both webinar eras
//                    ("- Webinar Applications" + "- Webinar", same UUID 6d150f24)
//                    now collapse to ONE bucket instead of two — bump evicts the
//                    stale two-bucket blobs. Route_lead trigger also repointed to
//                    the UUID gate (DB-only, no cached-payload impact).
// v245 (2026-09-21): STEP 2 owner_at_send flip of the first 2 easy fired-setter
//                    RPCs — get_setter_timeline (per-message owner, 2 alias pairs)
//                    and get_setter_precalls_attributed (Calendly-DM match keys on
//                    owner_at_send). Setter-timeline row counts + precall DM
//                    attribution now shift (Norman down, Alice/Patil up), so cached
//                    DM/setter payloads are stale — bump evicts them.
// v246 (2026-09-22): Rayston dialer commission rates corrected in the DB for two
//                    phone setters, Wintha (7c73997c) and Ryan Albino (d5d20fe2),
//                    from the closer/default rate to the dialer standard
//                    (commission_base 0.02 solo, self_set_commission_base 0.05
//                    create) matching Alyssa + Samantha; Wintha base_pay_monthly
//                    set to 1200. The cached RepInvoiceData blob bakes each rep's
//                    computed commission_rate / self_set_commission_rate plus line
//                    amounts, so it held the stale 10% figures (Wintha rendered
//                    $1,555.26 commission vs the correct ~$627.63) — bump evicts.
// v247 (2026-09-23): invoice dialer-commission fixes. BUG-B released/after-sale claims
//                    no longer pay "create" 5%; BUG-C pre-Aug-10 payment-plan
//                    installments pay the dialer $0; BUG-A shared leads now pay the
//                    dialer AND the DM setter 2.5% each (new RepSale.overlap_side).
//                    Cached RepInvoiceData blobs hold the old sales/buckets — evict.
// v248 (2026-09-28): STEP 2 S3 batch 1 — 5 conversation-grained DM RPCs now
//                    credit the TRUE owner via the new fired_setter_conv_owner
//                    override table (COALESCE(ovr.canonical_owner,
//                    c.ghl_assigned_user_id)): get_dismissed_leaderboard,
//                    get_engaged_per_setter, get_setter_conversation_counts,
//                    get_setter_cross_day_metrics, get_rep_portal_data. Payload
//                    shape unchanged; values move (Norman down ~150-360 convos per
//                    metric, Alice/Patil up, +19..+97 previously-unassigned convos
//                    join). Cached DM/portal blobs still credit Norman — evict.
// v249 (2026-09-28): get_dialer_scorecards gains a talk_time_sec key per dialer
//                    (SUM(daily_dial_metrics.talk_time_sec) over range) for the new
//                    "Talk time / active day" scorecard row (course KPI, 60+ min a
//                    day). Cached scorecard arrays lack the key, so the row would
//                    read 0 min for every dialer until evicted.
// v250 (2026-09-29): fired-setter sessions cluster (commits 9717528f + 8aaa9220).
//                    get_setter_sessions/daily_breakdown/hypnogram credit each
//                    message to owner_at_send; speed_to_lead/proof/proof_rows/
//                    reply_turn_distribution credit conversations via
//                    fired_setter_conv_owner; refresh_setter_daily_performance +
//                    get_setter_performance_fast follow. Shape unchanged; values move
//                    (Norman activity msgs 39,041 -> 35,513, speed-to-lead median
//                    21.9 -> 11.6 min, Alice/Patil up). Cached Setter Performance
//                    blobs still show phantom Norman sessions — evict.
// v251 (2026-10-01): Lost Leads show rate moves onto R14 + connected moves onto the
//                    /metrics 45s real connect. get_lost_leads_analytics gains an
//                    orphan_precalls count per group (migration
//                    2026-10-01_lost_leads_analytics_r14_show_rate.sql); the Analytics
//                    tab and the dashboard Revived card now divide shows by
//                    shows + no_shows + orphan_precalls (all-time 80.5% -> 55.7%).
//                    Cached analytics payloads lack the key and would keep the old
//                    two-term show rate — evict. (isConnected 45s change is client-only
//                    over an uncached dial payload; it rides along, needs no bump.)
// v252 (2026-10-01): Lost Leads revival rule C (Mako). New internal helpers
//                    booking_chain_root_at + get_claim_revivals (sql/claim_revivals.sql,
//                    migration 2026-10-01_lost_leads_revival_rule_c.sql). A claim is
//                    revived when a call was booked while the claim was LIVE (no /resched
//                    move of a pre-claim booking) and the claimer closed or set the sale.
//                    get_lost_leads_analytics gains revival_wins and redefines revival /
//                    revival_cash; get_lead_claim_history_json gains is_revival /
//                    revival_cash / revival_wins. All-time revived cash $159,851 ->
//                    $95,350; Nathaniel's uncredited $5,000 row goes to $0. Cached
//                    analytics payloads carry the old revival numbers and no
//                    revival_wins (Closes column would read 0) — evict.
// v253 (2026-10-01): fired-setter STEP 2, last 4 fns (22 of 22 settled). Shape
//                    unchanged; values move. get_setter_per_lead_reply_turns_window
//                    credits each message to owner_at_send and now includes convos a
//                    setter owned before reassignment (Norman window turns 28,279 ->
//                    27,178, Alice 11,538 -> 12,229, Patil 15,360 -> 15,976, Jun 1 -
//                    Sep 30). get_setter_unengaged_proof joins fired_setter_conv_owner
//                    (counts unchanged today). reattribute_/upsert_lead_funnel_source
//                    credit the send-time owner, dual-key join, setter_id now in the
//                    corrective UPDATE (Norman 48 -> 45, Patil 54 -> 57, Alice 53 -> 55
//                    setter-assisted leads). Cached reply-turn window blobs
//                    (useReplyTurnDistributionAll) hold the old owner split — evict.
// v254 (2026-10-01): Lost Leads close rates follow CREDIT + rule C sale date = the call
//                    (Mako: "close rate and cash collected that should all follow the
//                    credit", "sale date follows call start time"). get_claim_revivals
//                    gains credited_shows / no_shows / decisive / pending; new helper
//                    sco_sale_at() places an outcome by its call (half of outcome rows
//                    carry a date-only call_start_time). get_lost_leads_analytics gains
//                    revival_shows / revival_no_shows / revival_decisive /
//                    revival_pending and places window outcomes by call time (window
//                    wins 47 -> 44, credited totals unchanged 139 / $95,349.55 / 22).
//                    Final + Pending close now divide credited wins by credited decisive
//                    (all-time 22 / 51). Cached analytics payloads lack revival_decisive,
//                    so every close rate would read "—" — evict. Migration
//                    2026-10-01_lost_leads_credited_close_rate.sql. Carries the sibling
//                    v253 note above ([altara]_FIRED_SETTER_OWNERSHIP_SNAPSHOT.md).
// v255 (2026-10-03): Lost Leads Analytics reads stored claim facts (lla-facts P4).
//                    get_lost_leads_analytics now gates, repairs and aggregates the
//                    lost_lead_claim_facts table instead of recomputing every claim
//                    (server 3-4 s -> ~0.1 s). Values are unchanged (9 of 9 ranges
//                    equal the old reader), but the body, volatility (VOLATILE) and
//                    list tie-order changed, so evict per the query-change rule.
//                    Migration 2026-10-02_lla_facts_p4_reader.sql.
// v256 (2026-10-07): Daily KPI show-rate rule moves to R14 (Mako batch 10, SA7: "use the
//                    correct rule in /metrics"). evaluate_daily_kpi_compliance now divides
//                    shows by shows + no_shows + orphan pre-calls (compute_orphan_precalls,
//                    called live for the scored day) instead of + flakey + ghost; details
//                    .show_rate gains orphan_precalls and formula 'show_rate_r14'. Applied
//                    live 2026-10-06, future days only (21-day dry run: 4 of 56 stored marks
//                    would flip; past re-score is open card SA7b). Same push: the dialer
//                    podiums (LeaderboardPodium, LeaderboardPage) read real dials from
//                    get_dialer_scorecards (SA6) instead of daily_leaderboard.daily_dials,
//                    which is always 0. Cached leaderboard payloads carry KPI points under
//                    the old rule, so evict. Repo mirror sql/prestige/evaluate_daily_kpi_compliance.sql.
// v257 (2026-10-08): Lost Leads bar 6 + P4 money score, both live since the 10-08 push
//                    queue (items 20261007-02 bar6, 20261007-03 nt5-tier4). get_lost_leads_
//                    analytics now counts "wasted" under 6 tries and meta.min_attempts = 6
//                    (was 7), and refresh_lead_triage puts money score 8+ OR $5K+ in tier 4.
//                    Cached Analytics payloads (24 h historical TTL) still carry 7-based
//                    wasted counts, and cached pool rows the old tiers, so evict. Same commit
//                    adds the Partials tab (get_lost_leads_partials, queue item 20261007-02a)
//                    and the Webinar tab; both are new keys, nothing old to evict for them.
const MANUAL_SCHEMA_VERSION = "v257-lostleads-bar6-partials";

// Auto-derived from git commit SHA at build time (see vite.config.ts define block).
// Each deploy bumps this automatically. In dev/test environments where the constant
// isn't defined, falls back to "dev". Suffix appended so a manual bump invalidates
// dev caches too.
export const CACHE_VERSION =
  (typeof __BUILD_COMMIT__ !== "undefined" ? __BUILD_COMMIT__ : "dev") +
  "-" +
  MANUAL_SCHEMA_VERSION;

// Key used to track the last-seen cache version in localStorage
const VERSION_TRACKING_KEY = "altara_cache_version";

/**
 * Evict ALL altara_* localStorage keys that don't match the current CACHE_VERSION.
 * Called once on app startup (from App.tsx) to prevent orphan keys from accumulating.
 * Also evicts unversioned altara_* data caches (inv_, lb_, pcc_) on version change.
 *
 * Safe keys that are never evicted:
 * - Supabase auth tokens (sb-*)
 * - Theme/UI preferences (altara_theme, altara_sidebar, altara_glass)
 * - Activity/date state (altara_last_activity, altara_global_date_range)
 * - Org/session state (altara_last_org_id, altara_dev_mode)
 */
export function evictStaleCaches(): void {
  try {
    const stored = localStorage.getItem(VERSION_TRACKING_KEY);
    if (stored === CACHE_VERSION) return; // Already on current version

    // Version changed (or first run) — evict all data caches
    const keysToRemove: string[] = [];
    const safePatterns = [
      VERSION_TRACKING_KEY,
      "altara_theme",
      "altara_sidebar",
      "altara_last_activity",
      "altara_glass",
      "altara_global_date_range",
      "altara_last_org_id",
      "altara_dev_mode",
      // Calendar smart status filter — user-customized inclusion set;
      // resetting on cache bump would force re-clicking after every deploy.
      "altara_calendar_status_filter",
      // Calendar Hide-Reschedules toggle — UI preference; preserve across deploys.
      "altara_calendar_hide_reschedules",
      "altara_calendar_mode",
      // Calendar Flake-Reschedules-only toggle — UI preference; preserve across deploys.
      "altara_calendar_flake_reschedules_only",
      // Display-currency toggle (USD/AUD) — exec UI preference, preserve across
      // deploys so Nathaniel/Mako don't reset to USD on every bump.
      "altara_currency",
      // Triage Booked-tab claimed-date range — UI preference; preserve across deploys.
      "altara_triage_booked_range",
      // Calendar "Make this my default" landing view. The viewer set it on
      // purpose, so neither a deploy nor a quota eviction may reset it.
      "altara_calendar_default",
    ];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      // Only touch altara_* keys — never sb-*, never non-altara
      if (!key.startsWith("altara_")) continue;
      // Preserve UI preference keys
      if (safePatterns.some((p) => key.startsWith(p))) continue;
      keysToRemove.push(key);
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }

    localStorage.setItem(VERSION_TRACKING_KEY, CACHE_VERSION);

    if (keysToRemove.length > 0) {
      console.warn(
        `[CacheVersion] Evicted ${keysToRemove.length} stale cache entries (${stored ?? "none"} → ${CACHE_VERSION})`
      );
    }
  } catch {
    // If localStorage itself is broken, nothing we can do
  }
}

/**
 * Emergency eviction — clear ALL altara_* data caches regardless of version.
 * Called when a localStorage.setItem() throws QuotaExceededError.
 * Preserves auth tokens and UI preferences.
 */
export function emergencyEvictCaches(): void {
  try {
    const keysToRemove: string[] = [];
    const safePatterns = [
      VERSION_TRACKING_KEY,
      "altara_theme",
      "altara_sidebar",
      "altara_last_activity",
      "altara_glass",
      "altara_global_date_range",
      "altara_last_org_id",
      "altara_dev_mode",
      // Calendar smart status filter — see evictStaleCaches comment.
      "altara_calendar_status_filter",
      // Calendar Hide-Reschedules toggle — see evictStaleCaches comment.
      "altara_calendar_hide_reschedules",
      "altara_calendar_mode",
      // Calendar Flake-Reschedules-only toggle — see evictStaleCaches comment.
      "altara_calendar_flake_reschedules_only",
      // Display-currency toggle (USD/AUD) — exec UI preference, preserve across
      // deploys so Nathaniel/Mako don't reset to USD on every bump.
      "altara_currency",
      // Triage Booked-tab claimed-date range — UI preference; preserve across deploys.
      "altara_triage_booked_range",
      // Calendar "Make this my default" landing view. The viewer set it on
      // purpose, so neither a deploy nor a quota eviction may reset it.
      "altara_calendar_default",
    ];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!key.startsWith("altara_")) continue;
      if (safePatterns.some((p) => key.startsWith(p))) continue;
      keysToRemove.push(key);
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }

    console.warn(
      `[CacheVersion] Emergency eviction: cleared ${keysToRemove.length} cache entries to free localStorage`
    );
  } catch {
    // Nothing to do
  }
}

// =============================================================================
// UPDATE-LANDED BREADCRUMB
// =============================================================================
// reloadAndWipe() navigates via location.replace() — anything painted before
// the reload is gone. To show a "New Altara update just landed" toast on the
// FRESH page, we drop a breadcrumb right before the reload and read it on boot.
//
// sessionStorage (NOT localStorage): it survives a same-tab navigation but is
// untouched by emergencyEvictCaches() (which only sweeps localStorage altara_*
// keys) and by reloadAndWipe()'s CacheStorage/SW layers — so the breadcrumb is
// guaranteed to survive the wipe. Cleared on first read so it fires exactly once.
const UPDATE_LANDED_KEY = "altara_update_landed";

/** Set right before a deploy-driven reloadAndWipe() so the next boot can toast. */
export function markUpdateLanded(): void {
  try {
    sessionStorage.setItem(UPDATE_LANDED_KEY, "1");
  } catch {
    // sessionStorage can throw in private-mode/quota edge cases — toast is
    // cosmetic, so swallow and skip the cue rather than block the reload.
  }
}

/** Read + clear the breadcrumb. Returns true exactly once after a deploy reload. */
export function consumeUpdateLanded(): boolean {
  try {
    if (sessionStorage.getItem(UPDATE_LANDED_KEY) === "1") {
      sessionStorage.removeItem(UPDATE_LANDED_KEY);
      return true;
    }
  } catch {
    // See markUpdateLanded — swallow and report "no update landed".
  }
  return false;
}

/**
 * Nuke all app data caches and reload. Used by the UpdateBanner's "Refresh now"
 * button after the version poller detects a new deploy. Preserves Supabase auth
 * tokens and UI preferences via the same safe-list as emergencyEvictCaches.
 *
 * Four cache layers, all handled here. Any one is sufficient — stacked for
 * belt-and-suspenders because the cost of a stale reload is a confused dialer
 * mid-call, and the cost of all four is a few extra ms before navigation.
 *
 * 1. localStorage altara_* data keys → emergencyEvictCaches()
 * 2. CacheStorage (SW precache of HTML/JS/CSS) → caches.delete(*)
 * 3. Active service worker → unregister() so it stops controlling fetches
 * 4. URL cache-bust → location.replace() to a unique URL the browser cannot
 *    serve from any cache layer (HTTP cache, memory cache, SW interception)
 *
 * Why layer 4 even with 1–3: vite-plugin-pwa registerType:"autoUpdate" means
 * the OLD SW remains the page controller until the page unloads. unregister()
 * is queued, not immediate. A bare reload() with the same URL can race the
 * unregister and still hit the dying SW's fetch handler. A unique URL makes
 * the race irrelevant — even if intercepted, CacheStorage is empty (layer 2)
 * AND the URL has never been cached. Forces a network fetch unconditionally.
 *
 * HashRouter compatibility: route lives in the URL fragment (#/admin/...).
 * URL constructor parses fragment as `hash`, search params modify the
 * pre-fragment portion. After set(), URL becomes
 * `https://host/?_v=<ts>#/admin/transactions` — Vercel SPA rewrite serves
 * index.html on `/`, HashRouter reads the hash, route is preserved.
 */
export async function reloadAndWipe(): Promise<void> {
  emergencyEvictCaches();

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      // Best-effort; navigate anyway
    }
  }

  if ("serviceWorker" in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    } catch {
      // Best-effort; navigate anyway
    }
  }

  // Cache-bust the navigation. `_v` is overwritten on each click so the param
  // never accumulates. `replace()` (vs `assign()`) avoids polluting history
  // so the user cannot back-button into the broken-cache state.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    // URL constructor only throws on malformed input, which window.location
    // never produces. Fall back to a plain reload so the user is not stranded.
    window.location.reload();
  }
}
